import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Grid, Card, CardContent, Typography, TextField, Button, MenuItem,
    Select, FormControl, InputLabel, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, Chip, Alert, Dialog, DialogTitle, DialogContent,
    DialogActions, FormControlLabel, Switch, IconButton, Divider, InputAdornment, TablePagination
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import LayersIcon from '@mui/icons-material/Layers';
import WarningIcon from '@mui/icons-material/Warning';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import LockIcon from '@mui/icons-material/Lock';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import TableBarIcon from '@mui/icons-material/TableBar';
import DownloadIcon from '@mui/icons-material/Download';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import TableChartIcon from '@mui/icons-material/TableChart';
import ListAltIcon from '@mui/icons-material/ListAlt';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useApp } from '../context/AppContext';
import { useLanguage } from '../context/LanguageContext';

import API_BASE_URL from '../config/api';

function TableSectionManager() {
    const { sections, setSections, tables, setTables, tenantSubdomain, showToast, currentUser } = useApp();
    const [secName, setSecName] = useState('');
    const [tabId, setTabId] = useState('');
    const [tabNum, setTabNum] = useState('');
    const [tabSec, setTabSec] = useState('');
    const [tabOrder, setTabOrder] = useState('');

    // Edit state
    const [editingTableId, setEditingTableId] = useState(null);
    const [editForm, setEditForm] = useState({ number: '', section_id: '', display_order: 0 });

    const maxTables = currentUser?.max_tables || 10;
    const maxSections = currentUser?.max_sections || 3;

    const addSection = async () => {
        if (!secName) return;
        if (sections.length >= maxSections) {
            showToast(`Section limit reached (Max: ${maxSections}). Please upgrade your plan.`);
            return;
        }
        const maxOrder = sections.length > 0 ? Math.max(...sections.map(s => parseInt(s.display_order) || 0)) : 0;
        const newOrder = maxOrder + 1;
        const res = await fetch(`${API_BASE_URL}/tenant/sections`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Tenant-Subdomain': tenantSubdomain },
            body: JSON.stringify({ name: secName, display_order: newOrder, branch_id: currentUser?.branch_id || 1 })
        });
        const data = await res.json();
        if (data.success) {
            setSections([...sections, { id: data.id, name: secName, display_order: newOrder }]);
            setSecName('');
            showToast('Section added!');
        } else {
            showToast(data.error || 'Failed to add section');
        }
    };

    const addTable = async () => {
        if (!tabId || !tabNum) return;
        const currentTablesCount = tables.filter(t => t.id !== 'counter').length;
        if (currentTablesCount >= maxTables) {
            showToast(`Table limit reached (Max: ${maxTables}). Please upgrade your plan.`);
            return;
        }
        const maxOrd = tables.length > 0 ? Math.max(...tables.filter(t => t.id !== 'counter').map(t => parseInt(t.display_order) || 0)) : 0;
        const newDisplayOrder = parseInt(tabOrder) || (maxOrd + 1);
        const res = await fetch(`${API_BASE_URL}/tenant/tables`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Tenant-Subdomain': tenantSubdomain },
            body: JSON.stringify({ id: tabId, number: tabNum, section_id: tabSec || null, display_order: newDisplayOrder, branch_id: currentUser?.branch_id || 1 })
        });
        const data = await res.json();
        if (data.success) {
            setTables([...tables, { id: tabId, number: tabNum, section_id: tabSec || null, status: 'vacant', activeOrderId: null, display_order: newDisplayOrder }]);
            setTabId(''); setTabNum(''); setTabSec(''); setTabOrder('');
            showToast('Table added!');
        } else {
            showToast(data.error || 'Failed to add table (ID might be duplicate)');
        }
    };

    const startEditTable = (t) => {
        setEditingTableId(t.id);
        setEditForm({
            number: t.number || '',
            section_id: t.section_id || '',
            display_order: t.display_order || 0
        });
    };

    const saveEditTable = async () => {
        if (!editingTableId || !editForm.number) return;
        try {
            const res = await fetch(`${API_BASE_URL}/tenant/tables/${editingTableId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'X-Tenant-Subdomain': tenantSubdomain },
                body: JSON.stringify({
                    number: editForm.number,
                    section_id: editForm.section_id || null,
                    display_order: parseInt(editForm.display_order) || 0
                })
            });
            const data = await res.json();
            if (data.success) {
                setTables(tables.map(t => t.id === editingTableId ? {
                    ...t,
                    number: editForm.number,
                    section_id: editForm.section_id || null,
                    display_order: parseInt(editForm.display_order) || 0
                } : t));
                setEditingTableId(null);
                showToast('✅ Table updated!');
            } else {
                showToast(data.error || 'Failed to update table');
            }
        } catch (err) {
            showToast('Error: ' + err.message);
        }
    };

    const deleteTable = async (id) => {
        if (!window.confirm('Delete this table?')) return;
        const res = await fetch(`${API_BASE_URL}/tenant/tables/${id}`, {
            method: 'DELETE',
            headers: { 'X-Tenant-Subdomain': tenantSubdomain }
        });
        if (res.ok) {
            setTables(tables.filter(t => t.id !== id));
            showToast('Table deleted');
        }
    };

    const deleteSection = async (id) => {
        if (!window.confirm('Delete this section? Tables inside will lose their section assignment.')) return;
        const res = await fetch(`${API_BASE_URL}/tenant/sections/${id}`, {
            method: 'DELETE',
            headers: { 'X-Tenant-Subdomain': tenantSubdomain }
        });
        if (res.ok) {
            setSections(sections.filter(s => s.id !== id));
            showToast('Section deleted');
        }
    };

    // Sort tables by display_order for floor plan
    const sortedTables = [...tables].filter(t => !t.isSubTable && t.id !== 'counter')
        .sort((a, b) => (parseInt(a.display_order) || 0) - (parseInt(b.display_order) || 0));

    return (
        <Grid container spacing={4}>
            <Grid item xs={12} md={5}>
                <Card sx={{ borderRadius: 3, boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}><CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>Dining Areas (Sections)</Typography>
                    <Box sx={{ display: 'flex', gap: 1.5, mb: 3 }}>
                        <TextField size="small" fullWidth label="Section Name (e.g. AC Room)" value={secName} onChange={e => setSecName(e.target.value)} inputProps={{ autoComplete: 'off', autoCorrect: 'off', spellCheck: 'false', style: { touchAction: 'manipulation' } }} />
                        <Button variant="contained" onClick={addSection} sx={{ bgcolor: '#008751', '&:hover': { bgcolor: '#006f42' }, fontWeight: 800 }}>Add</Button>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {sections.map(s => (
                            <Chip
                                key={s.id}
                                label={s.name}
                                onDelete={() => deleteSection(s.id)}
                                sx={{ fontWeight: 700, borderRadius: 2 }}
                            />
                        ))}
                    </Box>
                </CardContent></Card>
            </Grid>
            <Grid item xs={12} md={7}>
                <Card sx={{ borderRadius: 3, boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}><CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>Tables Setup</Typography>
                    <Grid container spacing={1.5} sx={{ mb: 3 }}>
                        <Grid item xs={6} sm={3}>
                            <TextField size="small" fullWidth label="Unique ID (e.g. AC-1)" value={tabId} onChange={e => setTabId(e.target.value)} inputProps={{ autoComplete: 'off', autoCorrect: 'off', autoCapitalize: 'none', spellCheck: 'false', style: { touchAction: 'manipulation' } }} />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField size="small" fullWidth label="Display Name (e.g. Table 1)" value={tabNum} onChange={e => setTabNum(e.target.value)} inputProps={{ autoComplete: 'off', autoCorrect: 'off', spellCheck: 'false', style: { touchAction: 'manipulation' } }} />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <FormControl size="small" fullWidth>
                                <InputLabel>Section</InputLabel>
                                <Select value={tabSec} label="Section" onChange={e => setTabSec(e.target.value)}>
                                    <MenuItem value="">None</MenuItem>
                                    {sections.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField size="small" fullWidth label="Display Order" type="number" value={tabOrder} onChange={e => setTabOrder(e.target.value)} inputProps={{ style: { touchAction: 'manipulation' } }} />
                        </Grid>
                        <Grid item xs={12}>
                            <Button variant="contained" fullWidth onClick={addTable} sx={{ bgcolor: '#008751', '&:hover': { bgcolor: '#006f42' }, fontWeight: 800, mt: 1 }}>
                                Create Table
                            </Button>
                        </Grid>
                    </Grid>

                    <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e5e7eb', borderRadius: 2 }}>
                        <Table size="small">
                            <TableHead sx={{ bgcolor: '#f9fafb' }}><TableRow>
                                <TableCell sx={{ fontWeight: 800 }}>ID</TableCell>
                                <TableCell sx={{ fontWeight: 800 }}>Display Name</TableCell>
                                <TableCell sx={{ fontWeight: 800 }}>Section</TableCell>
                                <TableCell sx={{ fontWeight: 800 }}>Order</TableCell>
                                <TableCell sx={{ fontWeight: 800 }} align="right">Action</TableCell>
                            </TableRow></TableHead>
                            <TableBody>
                                {sortedTables.map(t => (
                                    <TableRow key={t.id}>
                                        <TableCell sx={{ fontWeight: 600 }}>{t.id}</TableCell>
                                        <TableCell>
                                            {editingTableId === t.id ? (
                                                <TextField size="small" value={editForm.number} onChange={e => setEditForm(p => ({ ...p, number: e.target.value }))} inputProps={{ style: { padding: '4px 8px' } }} />
                                            ) : t.number}
                                        </TableCell>
                                        <TableCell>
                                            {editingTableId === t.id ? (
                                                <FormControl size="small" sx={{ minWidth: 100 }}>
                                                    <Select value={editForm.section_id} onChange={e => setEditForm(p => ({ ...p, section_id: e.target.value }))} displayEmpty sx={{ '& .MuiSelect-select': { py: '4px' } }}>
                                                        <MenuItem value="">None</MenuItem>
                                                        {sections.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
                                                    </Select>
                                                </FormControl>
                                            ) : (
                                                <Chip size="small" label={sections.find(s => s.id === t.section_id)?.name || 'None'} sx={{ borderRadius: 1 }} />
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {editingTableId === t.id ? (
                                                <TextField size="small" type="number" value={editForm.display_order} onChange={e => setEditForm(p => ({ ...p, display_order: e.target.value }))} inputProps={{ style: { padding: '4px 8px', width: 50 } }} />
                                            ) : (parseInt(t.display_order) || 0)}
                                        </TableCell>
                                        <TableCell align="right">
                                            {editingTableId === t.id ? (
                                                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                                    <IconButton size="small" color="success" onClick={saveEditTable}><CheckIcon fontSize="small" /></IconButton>
                                                    <IconButton size="small" onClick={() => setEditingTableId(null)}><CloseIcon fontSize="small" /></IconButton>
                                                </Box>
                                            ) : (
                                                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                                    <IconButton size="small" color="primary" onClick={() => startEditTable(t)}><EditIcon fontSize="small" /></IconButton>
                                                    <IconButton size="small" color="error" onClick={() => deleteTable(t.id)}><DeleteIcon fontSize="small" /></IconButton>
                                                </Box>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent></Card>
            </Grid>
        </Grid>
    );
}

const GridRowItem = React.memo(({ 
    row, 
    rIdx, 
    categories, 
    currentLanguage, 
    isDirty, 
    saveStatus, 
    onCellChange, 
    onSaveRow, 
    onDeleteRow, 
    onAddRowBelow 
}) => {
    const rowKey = row.id || row._tempId;

    return (
        <TableRow 
            sx={{ 
                bgcolor: isDirty ? '#fffbeb' : (rIdx % 2 === 0 ? '#ffffff' : '#f8fafc'),
                '&:hover': { bgcolor: '#f0fdf4' }
            }}
        >
            {/* 1. Display Order */}
            <TableCell align="center">
                <input
                    type="number"
                    value={row.display_order !== undefined ? row.display_order : ''}
                    onChange={(e) => onCellChange(rowKey, 'display_order', e.target.value)}
                    style={{
                        width: '52px',
                        padding: '5px 4px',
                        textAlign: 'center',
                        fontWeight: 800,
                        fontSize: '0.85rem',
                        border: '1px solid #cbd5e1',
                        borderRadius: '6px',
                        outline: 'none',
                        backgroundColor: '#ffffff'
                    }}
                />
            </TableCell>

            {/* 2. English Dish Name */}
            <TableCell>
                <input
                    type="text"
                    placeholder="Dish Name (e.g. Paneer Butter Masala)"
                    value={row.name || ''}
                    onChange={(e) => onCellChange(rowKey, 'name', e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') onAddRowBelow(rIdx);
                    }}
                    style={{
                        width: '100%',
                        padding: '6px 10px',
                        fontWeight: 700,
                        fontSize: '0.88rem',
                        border: '1px solid #cbd5e1',
                        borderRadius: '6px',
                        outline: 'none',
                        backgroundColor: '#ffffff'
                    }}
                />
            </TableCell>

            {/* 3. Marathi Name */}
            <TableCell>
                <input
                    type="text"
                    placeholder="मराठी नाव (उदा. पनीर बटर मसाला)"
                    value={row.name_mr || ''}
                    onChange={(e) => onCellChange(rowKey, 'name_mr', e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') onAddRowBelow(rIdx);
                    }}
                    style={{
                        width: '100%',
                        padding: '6px 10px',
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        border: '1px solid #cbd5e1',
                        borderRadius: '6px',
                        outline: 'none',
                        backgroundColor: '#ffffff'
                    }}
                />
            </TableCell>

            {/* 4. Hindi Name */}
            <TableCell>
                <input
                    type="text"
                    placeholder="हिन्दी नाम (उदा. पनीर बटर मसाला)"
                    value={row.name_hi || ''}
                    onChange={(e) => onCellChange(rowKey, 'name_hi', e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') onAddRowBelow(rIdx);
                    }}
                    style={{
                        width: '100%',
                        padding: '6px 10px',
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        border: '1px solid #cbd5e1',
                        borderRadius: '6px',
                        outline: 'none',
                        backgroundColor: '#ffffff'
                    }}
                />
            </TableCell>

            {/* 5. Category Select (Native, 100x Faster!) */}
            <TableCell>
                <select
                    value={row.category_id || (categories && categories.length > 0 ? categories[0].id : '')}
                    onChange={(e) => onCellChange(rowKey, 'category_id', e.target.value)}
                    style={{
                        width: '100%',
                        padding: '6px 8px',
                        fontWeight: 700,
                        fontSize: '0.82rem',
                        border: '1px solid #cbd5e1',
                        borderRadius: '6px',
                        outline: 'none',
                        backgroundColor: '#ffffff',
                        cursor: 'pointer'
                    }}
                >
                    {categories && categories.map(cat => (
                        <option key={cat.id} value={cat.id}>
                            {currentLanguage === 'mr' ? (cat.name_mr || cat.name) : cat.name}
                        </option>
                    ))}
                </select>
            </TableCell>

            {/* 5. Price Input */}
            <TableCell align="right">
                <input
                    type="number"
                    placeholder="0.00"
                    value={row.price !== undefined ? row.price : ''}
                    onChange={(e) => onCellChange(rowKey, 'price', e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            onSaveRow(row);
                            onAddRowBelow(rIdx);
                        }
                    }}
                    style={{
                        width: '90px',
                        padding: '6px 8px',
                        textAlign: 'right',
                        fontWeight: 800,
                        fontSize: '0.88rem',
                        color: '#008751',
                        border: '1px solid #cbd5e1',
                        borderRadius: '6px',
                        outline: 'none',
                        backgroundColor: '#ffffff'
                    }}
                />
            </TableCell>

            {/* 6. Veg / Non-Veg Toggle */}
            <TableCell align="center">
                <button
                    type="button"
                    onClick={() => onCellChange(rowKey, 'is_veg', row.is_veg ? 0 : 1)}
                    style={{
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '0.72rem',
                        fontWeight: 800,
                        cursor: 'pointer',
                        backgroundColor: row.is_veg ? '#f0fdf4' : '#fef2f2',
                        color: row.is_veg ? '#166534' : '#991b1b',
                        border: row.is_veg ? '1px solid #bbf7d0' : '1px solid #fecaca'
                    }}
                >
                    {row.is_veg ? "🟢 Veg" : "🔴 Non-Veg"}
                </button>
            </TableCell>

            {/* 7. Active Status Toggle */}
            <TableCell align="center">
                <input
                    type="checkbox"
                    checked={row.is_available === 1 || row.is_available === true}
                    onChange={(e) => onCellChange(rowKey, 'is_available', e.target.checked ? 1 : 0)}
                    style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#008751' }}
                />
            </TableCell>

            {/* 8. Actions (Row-Level Instant Save & Delete) */}
            <TableCell align="center">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                    <Button
                        size="small"
                        variant={isDirty ? "contained" : "outlined"}
                        color={saveStatus === 'saved' ? "success" : (isDirty ? "warning" : "inherit")}
                        onClick={() => onSaveRow(row)}
                        disabled={saveStatus === 'saving'}
                        startIcon={saveStatus === 'saved' ? <CheckCircleIcon sx={{ fontSize: '14px !important' }} /> : <SaveIcon sx={{ fontSize: '14px !important' }} />}
                        sx={{
                            minWidth: 78,
                            py: 0.3,
                            px: 0.8,
                            fontSize: '0.72rem',
                            fontWeight: 800,
                            borderRadius: 1.5,
                            textTransform: 'none',
                            bgcolor: saveStatus === 'saved' ? '#10b981 !important' : (isDirty ? '#f59e0b' : 'transparent'),
                            color: isDirty || saveStatus === 'saved' ? '#ffffff' : '#475569',
                            borderColor: isDirty ? '#f59e0b' : '#cbd5e1'
                        }}
                    >
                        {saveStatus === 'saving' ? '...' : (saveStatus === 'saved' ? 'Saved' : (isDirty ? 'Save*' : 'Save'))}
                    </Button>
                    <IconButton
                        size="small"
                        color="error"
                        onClick={() => onDeleteRow(row)}
                        title="हटवा (Delete)"
                        sx={{ p: 0.4 }}
                    >
                        <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Box>
            </TableCell>
        </TableRow>
    );
});

export default function OwnerSettingsView() {
    const {
        menuItems, setMenuItems, categories, setCategories, recipes, inventory, updateMenuPrice, updateRecipeWeight, deleteRecipeIngredient, createInventoryItem,
        tenantSubdomain, showToast, addMenuItem, updateMenuItemDetails, deleteMenuItem,
        refreshMenuCatalog,
        showMenuImages, setShowMenuImages,
        posCardLayout, setPosCardLayout,
        cloudSyncEnabled, toggleCloudSync,
        currentUser
    } = useApp();
    const { t, currentLanguage } = useLanguage();

    const [selectedDishId, setSelectedDishId] = useState('');
    const [newIngId, setNewIngId] = useState('');
    const [newIngWeight, setNewIngWeight] = useState('');
    const [rawMatDialogOpen, setRawMatDialogOpen] = useState(false);
    const [rawMatName, setRawMatName] = useState('');
    const [rawMatUnit, setRawMatUnit] = useState('नग');
    const [rawMatQty, setRawMatQty] = useState('50');
    const [rawMatThreshold, setRawMatThreshold] = useState('10');

    const handleCreateRawMaterial = async () => {
        if (!rawMatName.trim()) {
            showToast('कृपया कच्च्या मालाचे नाव टाका (उदा. चपाती, चिकन वाटी, अंडी).');
            return;
        }
        const res = await createInventoryItem({
            name: rawMatName.trim(),
            unit: rawMatUnit,
            qty: parseFloat(rawMatQty) || 0,
            threshold: parseFloat(rawMatThreshold) || 5
        });
        if (res && res.success && res.item) {
            setNewIngId(res.item.id);
            setRawMatDialogOpen(false);
            setRawMatName('');
            setRawMatQty('50');
        }
    };
    const [priceEditState, setPriceEditState] = useState({});
    const [orderEditState, setOrderEditState] = useState({});
    const [weightEditState, setWeightEditState] = useState({});
    const [menuSearchQuery, setMenuSearchQuery] = useState('');
    const [menuPage, setMenuPage] = useState(0);
    const [menuRowsPerPage, setMenuRowsPerPage] = useState(25);
    const [dayCutoffTime, setDayCutoffTime] = useState(() => localStorage.getItem('dinexa_day_cutoff_time') || '04:00');

    const [selectedCategoryTab, setSelectedCategoryTab] = useState('all');

    // Excel Spreadsheet Grid Mode States
    const [menuViewMode, setMenuViewMode] = useState(() => {
        return localStorage.getItem('dinexa_menu_view_mode') || 'classic';
    });
    const [gridRows, setGridRows] = useState([]);
    const [dirtyRowIds, setDirtyRowIds] = useState({});
    const [rowSavingState, setRowSavingState] = useState({});
    const [batchSaving, setBatchSaving] = useState(false);
    const [gridSortDirection, setGridSortDirection] = useState('asc'); // 'asc' | 'desc'

    const handleSetMenuViewMode = (mode) => {
        setMenuViewMode(mode);
        localStorage.setItem('dinexa_menu_view_mode', mode);
    };

    const handleToggleOrderSort = () => {
        setGridSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    };

    const getActiveBranchId = () => {
        let storedUserBranch = null;
        try {
            const u = sessionStorage.getItem('react_pos_current_user');
            if (u) storedUserBranch = JSON.parse(u)?.branch_id;
        } catch(e) {}
        return currentUser?.branch_id || storedUserBranch || localStorage.getItem('dinexa_active_branch_id') || 1;
    };

    const activeBranchCategories = useMemo(() => {
        if (!categories || !Array.isArray(categories)) return [];
        const activeBranch = getActiveBranchId();
        return categories.filter(c => !c.branch_id || String(c.branch_id) === String(activeBranch));
    }, [categories, currentUser]);

    // Initialize gridRows from menuItems while preserving unsaved/new rows
    useEffect(() => {
        if (menuItems && Array.isArray(menuItems)) {
            const activeBranch = getActiveBranchId();
            const branchMenuItems = menuItems.filter(item => !item.branch_id || String(item.branch_id) === String(activeBranch));
            setGridRows(prev => {
                const unsavedNewRows = (prev || []).filter(r => r._isNew && dirtyRowIds[r._tempId]);
                const mappedExisting = branchMenuItems.map((item, idx) => {
                    const existingInPrev = (prev || []).find(p => p.id === item.id);
                    if (existingInPrev && dirtyRowIds[item.id]) {
                        return existingInPrev;
                    }
                    const rawOrder = item.display_order !== undefined && item.display_order !== null 
                        ? item.display_order 
                        : (item.displayOrder !== undefined && item.displayOrder !== null ? item.displayOrder : null);
                    
                    const finalOrder = (rawOrder !== null && rawOrder !== '' && !isNaN(parseInt(rawOrder))) 
                        ? parseInt(rawOrder) 
                        : (idx + 1);

                    return {
                        id: item.id,
                        branch_id: item.branch_id || activeBranch,
                        display_order: finalOrder,
                        name: item.name || '',
                        name_mr: item.nameMr || item.name_mr || '',
                        name_hi: item.nameHi || item.name_hi || '',
                        category_id: item.category_id || (activeBranchCategories.length > 0 ? activeBranchCategories[0].id : 1),
                        category: item.category || '',
                        price: item.price !== undefined ? item.price : 0,
                        is_veg: item.veg !== undefined ? (item.veg ? 1 : 0) : (item.is_veg !== undefined ? (item.is_veg ? 1 : 0) : 1),
                        is_available: item.isAvailable !== undefined ? (item.isAvailable ? 1 : 0) : (item.is_available !== undefined ? (item.is_available ? 1 : 0) : 1)
                    };
                });
                return [...mappedExisting, ...unsavedNewRows];
            });
        }
    }, [menuItems, categories, activeBranchCategories]);

    // Grid Cell Change Handler
    const handleGridCellChange = (rowKey, field, value) => {
        setGridRows(prev => prev.map(r => {
            const key = r.id || r._tempId;
            if (key === rowKey) {
                return { ...r, [field]: value };
            }
            return r;
        }));
        setDirtyRowIds(prev => ({ ...prev, [rowKey]: true }));
    };

    // Add Blank Grid Row(s)
    const handleAddGridRow = (count = 1) => {
        const newRows = [];
        const maxOrder = gridRows.length > 0 ? Math.max(...gridRows.map(r => parseInt(r.display_order) || 0)) : 0;
        const defaultCatId = selectedCategoryTab !== 'all' ? selectedCategoryTab : (categories && categories.length > 0 ? categories[0].id : 1);
        const activeBranch = getActiveBranchId();

        for (let i = 0; i < count; i++) {
            const tempId = `new_${Date.now()}_${Math.random().toString(36).substr(2, 5)}_${i}`;
            newRows.push({
                _tempId: tempId,
                id: null,
                branch_id: activeBranch,
                display_order: maxOrder + i + 1,
                name: '',
                name_mr: '',
                name_hi: '',
                category_id: defaultCatId,
                price: '',
                is_veg: 1,
                is_available: 1,
                _isNew: true
            });
        }
        setGridRows(prev => [...prev, ...newRows]);
        newRows.forEach(nr => {
            setDirtyRowIds(prev => ({ ...prev, [nr._tempId]: true }));
        });
        showToast(count === 1 ? "➕ नवीन पंक्ती जोडली!" : `➕ ${count} नवीन पंक्ती जोडल्या!`);
    };

    // Delete Grid Row
    const handleDeleteGridRow = async (row) => {
        const rowKey = row.id || row._tempId;
        if (row.id) {
            if (!window.confirm(`Are you sure you want to delete "${row.name || 'this item'}"?`)) return;
            await deleteMenuItem(row.id);
        }
        setGridRows(prev => prev.filter(r => (r.id || r._tempId) !== rowKey));
        setDirtyRowIds(prev => {
            const copy = { ...prev };
            delete copy[rowKey];
            return copy;
        });
        showToast("🗑️ पंक्ती हटवली!");
    };

    // Save Single Row (Row-Level Instant Save)
    const handleSaveSingleGridRow = async (row) => {
        const rowKey = row.id || row._tempId;
        if (!row.name && !row.price) {
            showToast("⚠️ कृपया डिशचे नाव किंवा दर प्रविष्ट करा.");
            return;
        }

        const activeBranch = getActiveBranchId();
        setRowSavingState(prev => ({ ...prev, [rowKey]: 'saving' }));
        try {
            const payload = {
                branch_id: parseInt(row.branch_id || activeBranch) || 1,
                name: row.name || 'General Dish',
                name_mr: row.name_mr || row.name || 'General Dish',
                name_hi: row.name_hi || row.name || 'General Dish',
                category_id: parseInt(row.category_id) || (categories[0]?.id || 1),
                price: parseFloat(row.price) || 0,
                display_order: parseInt(row.display_order) || 0,
                is_veg: row.is_veg === 1 || row.is_veg === true ? 1 : 0,
                veg: row.is_veg === 1 || row.is_veg === true,
                is_available: row.is_available === 1 || row.is_available === true ? 1 : 0
            };

            let savedId = row.id;
            if (row.id) {
                await updateMenuItemDetails(row.id, payload);
            } else {
                const res = await fetch(`${API_BASE_URL}/tenant/menu`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'X-Tenant-Subdomain': tenantSubdomain },
                    body: JSON.stringify(payload)
                });
                const data = await res.json();
                if (data.success && data.id) {
                    savedId = data.id;
                }
            }

            // Update local row state with saved ID
            setGridRows(prev => prev.map(r => {
                if ((r.id || r._tempId) === rowKey) {
                    return { ...r, id: savedId, _tempId: undefined, _isNew: false, ...payload };
                }
                return r;
            }));

            setDirtyRowIds(prev => {
                const copy = { ...prev };
                delete copy[rowKey];
                if (savedId) delete copy[savedId];
                return copy;
            });

            setRowSavingState(prev => ({ ...prev, [rowKey]: 'saved', [savedId]: 'saved' }));
            setTimeout(() => {
                setRowSavingState(prev => {
                    const copy = { ...prev };
                    delete copy[rowKey];
                    if (savedId) delete copy[savedId];
                    return copy;
                });
            }, 2000);

            showToast(`✅ "${payload.name}" थेट सेव्ह झाला!`);
            if (refreshMenuCatalog) refreshMenuCatalog();
        } catch (err) {
            console.error("Single row save error:", err);
            showToast("❌ सेव्ह करताना त्रुटी आली.");
            setRowSavingState(prev => {
                const copy = { ...prev };
                delete copy[rowKey];
                return copy;
            });
        }
    };

    // Global Batch Save (Save All Changes)
    const handleSaveAllGridChanges = async () => {
        const dirtyKeys = Object.keys(dirtyRowIds).filter(k => dirtyRowIds[k]);
        if (dirtyKeys.length === 0) {
            showToast("ℹ️ कोणतेही बदल सेव्ह करणे बाकी नाही.");
            return;
        }

        const itemsToSave = gridRows.filter(r => dirtyRowIds[r.id || r._tempId] && (r.name || r.price));
        if (itemsToSave.length === 0) {
            showToast("ℹ️ सेव्ह करण्यासाठी वैध नोंदी नाहीत.");
            return;
        }

        const activeBranch = getActiveBranchId();
        const itemsWithBranch = itemsToSave.map(item => ({
            ...item,
            is_veg: item.is_veg === 1 || item.is_veg === true ? 1 : 0,
            veg: item.is_veg === 1 || item.is_veg === true,
            is_available: item.is_available === 1 || item.is_available === true ? 1 : 0,
            branch_id: parseInt(item.branch_id || activeBranch) || 1
        }));

        setBatchSaving(true);
        try {
            const response = await fetch(`${API_BASE_URL}/tenant/menu/batch-save`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-Tenant-Subdomain': tenantSubdomain },
                body: JSON.stringify({ items: itemsWithBranch, branch_id: parseInt(activeBranch) || 1 })
            });
            const data = await response.json();
            if (data.success) {
                showToast(`✅ ${data.message || 'सर्व मेन्यू नोंदी सेव्ह झाल्या!'}`);
                setDirtyRowIds({});
                if (refreshMenuCatalog) refreshMenuCatalog();
            } else {
                showToast(`❌ ${data.error || 'Failed to save menu items'}`);
            }
        } catch (err) {
            console.error("Batch save error:", err);
            showToast("❌ Network error during batch save.");
        } finally {
            setBatchSaving(false);
        }
    };

    // Filtered Grid Rows
    const filteredGridRows = useMemo(() => {
        let list = [...gridRows];

        if (selectedCategoryTab !== 'all') {
            const catObj = categories && Array.isArray(categories) ? categories.find(c => c.id == selectedCategoryTab || String(c.name).toLowerCase() === String(selectedCategoryTab).toLowerCase()) : null;
            const targetCatName = catObj ? (catObj.name || '').toLowerCase() : '';

            list = list.filter(item => {
                const itemCatName = (item.category || item.category_name || '').toLowerCase();
                return String(item.category_id) === String(selectedCategoryTab) || 
                    String(item.category) === String(selectedCategoryTab) ||
                    (targetCatName !== '' && itemCatName === targetCatName) ||
                    (catObj && catObj.id == item.category_id);
            });
        }

        if (menuSearchQuery) {
            const q = menuSearchQuery.toLowerCase().trim();
            list = list.filter(item => {
                const nameEn = (item.name || '').toLowerCase();
                const nameMr = (item.name_mr || '').toLowerCase();
                const nameHi = (item.name_hi || '').toLowerCase();
                const catName = (item.category || item.category_name || '').toLowerCase();
                return nameEn.includes(q) || nameMr.includes(q) || nameHi.includes(q) || catName.includes(q);
            });
        }

        return list.sort((a, b) => {
            const orderA = parseInt(a.display_order !== undefined && a.display_order !== null ? a.display_order : a.displayOrder) || 0;
            const orderB = parseInt(b.display_order !== undefined && b.display_order !== null ? b.display_order : b.displayOrder) || 0;
            if (orderA !== orderB) {
                return gridSortDirection === 'asc' ? orderA - orderB : orderB - orderA;
            }
            return gridSortDirection === 'asc' 
                ? ((parseInt(a.id) || 0) - (parseInt(b.id) || 0)) 
                : ((parseInt(b.id) || 0) - (parseInt(a.id) || 0));
        });
    }, [gridRows, selectedCategoryTab, menuSearchQuery, categories, gridSortDirection]);

    useEffect(() => {
        if (menuItems && menuItems.length > 0) {
            const exists = menuItems.some(item => parseInt(item.id) === parseInt(selectedDishId));
            if (!exists) {
                setSelectedDishId(menuItems[0].id);
            }
        }
    }, [menuItems, selectedDishId]);

    const filteredMenuItems = useMemo(() => {
        if (!menuItems) return [];
        const activeBranch = getActiveBranchId();
        let list = menuItems.filter(item => !item.branch_id || String(item.branch_id) === String(activeBranch));

        // 1. Filter by category tab
        if (selectedCategoryTab !== 'all') {
            const catObj = activeBranchCategories && Array.isArray(activeBranchCategories) ? activeBranchCategories.find(c => c.id == selectedCategoryTab || c.name.toString().toLowerCase() === selectedCategoryTab.toString().toLowerCase()) : null;
            const targetCatName = catObj ? (catObj.name || '').toString().toLowerCase() : '';

            list = list.filter(item => {
                const itemCatName = (item.category || item.category_name || '').toString().toLowerCase();
                return item.category_id == selectedCategoryTab || 
                    item.category == selectedCategoryTab ||
                    (targetCatName !== '' && itemCatName === targetCatName) ||
                    (catObj && catObj.id == item.category_id);
            });
        }

        // 2. Filter by search query
        if (menuSearchQuery) {
            const q = menuSearchQuery.toLowerCase().trim();
            list = list.filter(item => {
                const nameEn = (item.name || '').toLowerCase();
                const nameMr = (item.nameMr || item.name_mr || '').toLowerCase();
                const nameHi = (item.nameHi || item.name_hi || '').toLowerCase();
                const catName = (item.category || item.category_name || '').toLowerCase();
                return nameEn.includes(q) || nameMr.includes(q) || nameHi.includes(q) || catName.includes(q);
            });
        }

        // 3. Sort by display_order sequentially
        return list.sort((a, b) => {
            const orderA = parseInt(a.display_order !== undefined ? a.display_order : a.displayOrder) || 0;
            const orderB = parseInt(b.display_order !== undefined ? b.display_order : b.displayOrder) || 0;
            if (orderA !== orderB) return orderA - orderB;
            return parseInt(a.id) - parseInt(b.id);
        });
    }, [menuItems, menuSearchQuery, selectedCategoryTab]);

    const paginatedMenuItems = useMemo(() => {
        const start = menuPage * menuRowsPerPage;
        return filteredMenuItems.slice(start, start + menuRowsPerPage);
    }, [filteredMenuItems, menuPage, menuRowsPerPage]);

    const handleMoveDishOrder = async (item, direction) => {
        const itemCatId = item.category_id || item.category;
        const categoryDishes = menuItems
            .filter(m => (m.category_id == itemCatId || m.category == itemCatId))
            .sort((a, b) => (parseInt(a.display_order) || 0) - (parseInt(b.display_order) || 0));

        const currentIndex = categoryDishes.findIndex(m => m.id === item.id);
        if (currentIndex === -1) return;

        const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
        if (targetIndex < 0 || targetIndex >= categoryDishes.length) return;

        const otherItem = categoryDishes[targetIndex];

        const currentOrder = parseInt(item.display_order) || (currentIndex + 1);
        const otherOrder = parseInt(otherItem.display_order) || (targetIndex + 1);

        const newCurrentOrder = otherOrder;
        const newOtherOrder = currentOrder !== otherOrder ? currentOrder : (direction === 'up' ? otherOrder - 1 : otherOrder + 1);

        const updatedMenuItems = menuItems.map(m => {
            if (m.id === item.id) return { ...m, display_order: newCurrentOrder };
            if (m.id === otherItem.id) return { ...m, display_order: newOtherOrder };
            return m;
        });

        if (typeof setMenuItems === 'function') {
            setMenuItems(updatedMenuItems);
        }

        showToast(`📌 ${item.name} चा क्रम बदलला! (#${newCurrentOrder})`);

        try {
            await fetch(`${API_BASE_URL}/tenant/menu/${item.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'X-Tenant-Subdomain': tenantSubdomain },
                body: JSON.stringify({ display_order: newCurrentOrder })
            });
            await fetch(`${API_BASE_URL}/tenant/menu/${otherItem.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'X-Tenant-Subdomain': tenantSubdomain },
                body: JSON.stringify({ display_order: newOtherOrder })
            });
            if (refreshMenuCatalog) refreshMenuCatalog();
        } catch (err) {
            console.error("Failed to sync menu item order:", err);
        }
    };

    const handleOrderChange = (itemId, val) => {
        setOrderEditState(prev => ({ ...prev, [itemId]: val }));
    };

    const handleSaveOrder = async (item) => {
        const newOrderRaw = orderEditState[item.id];
        if (newOrderRaw === undefined || newOrderRaw === '') return;
        const newOrderNum = parseInt(newOrderRaw, 10);
        if (isNaN(newOrderNum) || newOrderNum < 1) {
            showToast("⚠️ कृपया योग्य क्रमांक टाका (उदा. 1, 2, 3...)");
            return;
        }

        const updatedMenuItems = menuItems.map(m => {
            if (m.id === item.id) return { ...m, display_order: newOrderNum };
            return m;
        });

        if (typeof setMenuItems === 'function') {
            setMenuItems(updatedMenuItems);
        }

        setOrderEditState(prev => {
            const next = { ...prev };
            delete next[item.id];
            return next;
        });

        showToast(`📌 ${item.name} चा नवीन अनुक्रम सेट केला! (#${newOrderNum})`);

        try {
            await fetch(`${API_BASE_URL}/tenant/menu/${item.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'X-Tenant-Subdomain': tenantSubdomain },
                body: JSON.stringify({ display_order: newOrderNum })
            });
            if (refreshMenuCatalog) refreshMenuCatalog();
        } catch (err) {
            console.error("Failed to sync menu item order:", err);
        }
    };

    const [selectedPreset, setSelectedPreset] = useState('multi-cuisine');
    const [presetLoading, setPresetLoading] = useState(false);

    // GST Billing State
    const [gstEnabled, setGstEnabled] = useState(() => {
        return localStorage.getItem('dinexa_gst_enabled') !== 'false';
    });

    // Tab state
    const [activeTab, setActiveTab] = useState('menu');

    // Dish Form State
    const [dishFormOpen, setDishFormOpen] = useState(false);
    const [editingDish, setEditingDish] = useState(null);
    const [dishForm, setDishForm] = useState({
        name: '',
        name_mr: '',
        name_hi: '',
        category_id: '',
        price: '',
        display_order: 0,
        is_available: true,
        image_url: ''
    });

    // Category Management States
    const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [newCategoryNameMr, setNewCategoryNameMr] = useState('');
    const [newCategoryDisplayOrder, setNewCategoryDisplayOrder] = useState(0);
    const [categorySaveLoading, setCategorySaveLoading] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [editCategoryName, setEditCategoryName] = useState('');
    const [editCategoryNameMr, setEditCategoryNameMr] = useState('');
    const [editCategoryDisplayOrder, setEditCategoryDisplayOrder] = useState(0);

    // CSV Export / Import States & Feature Flag
    const [isCsvFeatureEnabled, setIsCsvFeatureEnabled] = useState(true);
    const [csvPreviewOpen, setCsvPreviewOpen] = useState(false);
    const [csvParsedItems, setCsvParsedItems] = useState([]);
    const [csvImportLoading, setCsvImportLoading] = useState(false);

    useEffect(() => {
        const checkCsvModule = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/tenant/modules`, {
                    headers: { 'X-Tenant-Subdomain': tenantSubdomain }
                });
                const data = await response.json();
                if (data.success && Array.isArray(data.modules)) {
                    const csvMod = data.modules.find(m => m.module_name === 'menu_csv_export_import');
                    if (csvMod !== undefined) {
                        setIsCsvFeatureEnabled(csvMod.is_enabled === 1 || csvMod.is_enabled === true);
                    }
                }
            } catch (err) {
                setIsCsvFeatureEnabled(true);
            }
        };
        if (tenantSubdomain) {
            checkCsvModule();
        }
    }, [tenantSubdomain]);

    const getVegFlag = (item) => {
        const rawVeg = item.veg !== undefined ? item.veg : item.is_veg;
        if (rawVeg === true || rawVeg === 1 || rawVeg === '1') return 1;
        if (rawVeg === false || rawVeg === 0 || rawVeg === '0') {
            if (rawVeg === false || rawVeg === 0) return 0;
        }

        // Name-based smart detection fallback for legacy items
        const nameLower = `${item.name || ''} ${item.nameMr || item.name_mr || ''}`.toLowerCase();
        const nonVegKeywords = ['chicken', 'mutton', 'fish', 'egg', 'prawn', 'kabab', 'kebab', 'tandoori chicken', 'चिकन', 'मटण', 'अंडी', 'फिश', 'कोळंबी'];
        const isNonVeg = nonVegKeywords.some(kw => nameLower.includes(kw));

        return isNonVeg ? 0 : 1;
    };

    const exportMenuToCSV = () => {
        if (!menuItems || menuItems.length === 0) {
            showToast("No menu items available to export!");
            return;
        }

        const headers = ["id", "category_id", "category_name", "name", "name_mr", "name_hi", "price", "veg", "is_available", "display_order"];

        const rows = menuItems.map(item => {
            const catObj = categories.find(c => c.id == item.category_id) || {};
            const catName = item.category || catObj.name || 'General';
            const isVeg = getVegFlag(item);
            const avail = (item.isAvailable !== undefined ? item.isAvailable : (item.is_available !== undefined ? item.is_available : true)) ? 1 : 0;

            const escapeCSV = (val) => {
                if (val === null || val === undefined) return '';
                let str = String(val).replace(/"/g, '""');
                if (str.includes(',') || str.includes('\n') || str.includes('"')) {
                    str = `"${str}"`;
                }
                return str;
            };

            return [
                item.id || '',
                item.category_id || '',
                escapeCSV(catName),
                escapeCSV(item.name || ''),
                escapeCSV(item.nameMr || item.name_mr || ''),
                escapeCSV(item.nameHi || item.name_hi || ''),
                item.price || 0,
                isVeg,
                avail,
                item.display_order || item.displayOrder || 0
            ].join(',');
        });

        const csvContent = '\uFEFF' + [headers.join(','), ...rows].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `DineXa_Menu_Export_${tenantSubdomain || 'hotel'}_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        showToast("✅ Menu CSV file downloaded successfully!");
    };

    const handleCsvFileSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const text = evt.target.result;
                const lines = text.split(/\r\n|\n/);
                if (lines.length < 2) {
                    showToast("Uploaded CSV file is empty!");
                    return;
                }

                const parseCSVLine = (line) => {
                    const result = [];
                    let startValueIdx = 0;
                    let inQuotes = false;
                    for (let i = 0; i < line.length; i++) {
                        const c = line[i];
                        if (c === '"') {
                            inQuotes = !inQuotes;
                        } else if (c === ',' && !inQuotes) {
                            let val = line.substring(startValueIdx, i).trim();
                            if (val.startsWith('"') && val.endsWith('"')) {
                                val = val.slice(1, -1).replace(/""/g, '"');
                            }
                            result.push(val);
                            startValueIdx = i + 1;
                        }
                    }
                    let lastVal = line.substring(startValueIdx).trim();
                    if (lastVal.startsWith('"') && lastVal.endsWith('"')) {
                        lastVal = lastVal.slice(1, -1).replace(/""/g, '"');
                    }
                    result.push(lastVal);
                    return result;
                };

                const headers = parseCSVLine(lines[0]).map(h => h.toLowerCase().trim().replace(/^\uFEFF/, ''));

                const parsedRows = [];
                for (let i = 1; i < lines.length; i++) {
                    if (!lines[i].trim()) continue;
                    const cols = parseCSVLine(lines[i]);
                    const rowObj = {};
                    headers.forEach((h, idx) => {
                        rowObj[h] = cols[idx] !== undefined ? cols[idx] : '';
                    });

                    if (rowObj.name) {
                        let vegVal = 1;
                        if (rowObj.veg !== undefined && rowObj.veg !== '') {
                            const v = String(rowObj.veg).trim().toLowerCase();
                            vegVal = (v === '1' || v === 'true' || v === 'veg') ? 1 : 0;
                        } else {
                            const nameLower = `${rowObj.name || ''} ${rowObj.name_mr || ''}`.toLowerCase();
                            const nonVegKeywords = ['chicken', 'mutton', 'fish', 'egg', 'prawn', 'kabab', 'kebab', 'tandoori chicken', 'चिकन', 'मटण', 'अंडी', 'फिश', 'कोळंबी'];
                            vegVal = nonVegKeywords.some(kw => nameLower.includes(kw)) ? 0 : 1;
                        }

                        parsedRows.push({
                            id: rowObj.id ? parseInt(rowObj.id) : null,
                            category_id: rowObj.category_id ? parseInt(rowObj.category_id) : null,
                            category_name: rowObj.category_name || rowObj.category || 'General',
                            name: rowObj.name,
                            name_mr: rowObj.name_mr || rowObj.name,
                            name_hi: rowObj.name_hi || rowObj.name,
                            price: parseFloat(rowObj.price) || 0,
                            veg: vegVal,
                            is_available: rowObj.is_available !== '' ? parseInt(rowObj.is_available) : 1,
                            display_order: rowObj.display_order !== '' ? parseInt(rowObj.display_order) : 0
                        });
                    }
                }

                if (parsedRows.length === 0) {
                    showToast("No valid dish rows found in CSV!");
                    return;
                }

                setCsvParsedItems(parsedRows);
                setCsvPreviewOpen(true);
            } catch (err) {
                console.error("CSV parse error:", err);
                showToast("Failed to parse CSV file. Please ensure valid CSV format.");
            }
        };

        reader.readAsText(file, 'UTF-8');
        e.target.value = '';
    };

    const handleConfirmCsvImport = async () => {
        if (!csvParsedItems || csvParsedItems.length === 0) return;
        setCsvImportLoading(true);

        try {
            const response = await fetch(`${API_BASE_URL}/tenant/menu/bulk-import`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Tenant-Subdomain': tenantSubdomain
                },
                body: JSON.stringify({ items: csvParsedItems })
            });

            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                const text = await response.text();
                console.error("Non-JSON backend response:", text);
                showToast("Server API Error. Please upload backend TenantDataController.php & api.php to live server.");
                return;
            }

            const data = await response.json();
            if (data.success) {
                showToast(`✅ ${data.message || 'Menu imported successfully!'}`);
                setCsvPreviewOpen(false);
                setCsvParsedItems([]);
                if (refreshMenuCatalog) refreshMenuCatalog();

                const catRes = await fetch(`${API_BASE_URL}/tenant/categories`, {
                    headers: { 'X-Tenant-Subdomain': tenantSubdomain }
                });
                const catData = await catRes.json();
                if (catData.success) setCategories(catData.categories || []);
            } else {
                showToast(data.error || "Failed to import menu CSV.");
            }
        } catch (err) {
            console.error("CSV import error:", err);
            showToast("Network error during CSV import. Please verify backend server status.");
        } finally {
            setCsvImportLoading(false);
        }
    };

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/tenant/categories`, {
                    headers: { 'X-Tenant-Subdomain': tenantSubdomain }
                });
                const data = await response.json();
                if (data.success && Array.isArray(data.categories)) {
                    setCategories(prev => {
                        if (JSON.stringify(prev) !== JSON.stringify(data.categories)) {
                            return data.categories;
                        }
                        return prev;
                    });
                }
            } catch (err) {
                // Silent background sync failure
            }
        };
        if (tenantSubdomain) {
            fetchCategories();
        }
    }, [tenantSubdomain]);

    const reindexCategoryList = (list, changedId, requestedOrder) => {
        const targetPos = Math.max(1, parseInt(requestedOrder) || 1);
        let others = list.filter(c => c.id !== changedId);
        others.sort((a, b) => (parseInt(a.display_order) || 0) - (parseInt(b.display_order) || 0));

        const item = list.find(c => c.id === changedId);
        if (item) {
            const insertIdx = Math.min(Math.max(0, targetPos - 1), others.length);
            others.splice(insertIdx, 0, { ...item, display_order: targetPos });
        }

        return others.map((cat, idx) => ({
            ...cat,
            display_order: idx + 1
        }));
    };

    const syncCategoriesToBackend = async (reindexedList) => {
        const activeBranch = getActiveBranchId();
        for (const cat of reindexedList) {
            try {
                await fetch(`${API_BASE_URL}/tenant/categories/${cat.id}`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Tenant-Subdomain': tenantSubdomain
                    },
                    body: JSON.stringify({
                        name: cat.name,
                        name_mr: cat.name_mr || cat.name,
                        display_order: cat.display_order,
                        branch_id: cat.branch_id ? parseInt(cat.branch_id) : parseInt(activeBranch)
                    })
                });
            } catch (err) {
                console.error("Failed to sync category order:", cat.id, err);
            }
        }
    };

    const handleOpenAddCategory = () => {
        const maxOrder = activeBranchCategories.length > 0 ? Math.max(...activeBranchCategories.map(c => parseInt(c.display_order) || 0)) : 0;
        setNewCategoryDisplayOrder(maxOrder + 1);
        setNewCategoryName('');
        setNewCategoryNameMr('');
        setCategoryDialogOpen(true);
    };

    const handleCreateCategory = async (e) => {
        e.preventDefault();
        if (!newCategoryName) {
            showToast("Please enter a category name!");
            return;
        }
        setCategorySaveLoading(true);
        const activeBranch = getActiveBranchId();
        try {
            const targetOrder = parseInt(newCategoryDisplayOrder) || (activeBranchCategories.length + 1);
            const response = await fetch(`${API_BASE_URL}/tenant/categories`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Tenant-Subdomain': tenantSubdomain
                },
                body: JSON.stringify({
                    name: newCategoryName,
                    name_mr: newCategoryNameMr || newCategoryName,
                    display_order: targetOrder,
                    branch_id: parseInt(activeBranch) || 1
                })
            });
            const data = await response.json();
            if (data.success) {
                showToast("Category added successfully!");
                const reindexed = reindexCategoryList([...activeBranchCategories, data.category], data.category.id, targetOrder);
                setCategories(reindexed);
                syncCategoriesToBackend(reindexed);
                setNewCategoryName('');
                setNewCategoryNameMr('');
                setNewCategoryDisplayOrder(reindexed.length + 1);
                if (refreshMenuCatalog) refreshMenuCatalog();
            } else {
                showToast(data.error || "Failed to add category");
            }
        } catch (err) {
            showToast("Network error");
        } finally {
            setCategorySaveLoading(false);
        }
    };

    const handleUpdateCategory = async (e) => {
        e.preventDefault();
        if (!editCategoryName || !editingCategory) {
            showToast("Please enter a category name!");
            return;
        }
        try {
            const activeBranch = getActiveBranchId();
            const targetOrder = parseInt(editCategoryDisplayOrder) || 1;
            const updatedRaw = activeBranchCategories.map(c => c.id === editingCategory.id ? { ...c, name: editCategoryName, name_mr: editCategoryNameMr || editCategoryName, display_order: targetOrder, branch_id: parseInt(activeBranch) } : c);
            const reindexed = reindexCategoryList(updatedRaw, editingCategory.id, targetOrder);

            setCategories(reindexed);
            setEditingCategory(null);
            setEditCategoryName('');
            setEditCategoryNameMr('');
            setEditCategoryDisplayOrder(0);
            showToast("Category order updated and re-indexed!");

            await syncCategoriesToBackend(reindexed);
            if (refreshMenuCatalog) refreshMenuCatalog();
        } catch (err) {
            showToast("Network error");
        }
    };

    const handleDeleteCategory = async (catId) => {
        if (!window.confirm("WARNING: Deleting a category will delete all dishes inside it. Do you want to proceed?")) {
            return;
        }
        try {
            const activeBranch = getActiveBranchId();
            const response = await fetch(`${API_BASE_URL}/tenant/categories/${catId}?branch_id=${activeBranch}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Tenant-Subdomain': tenantSubdomain
                }
            });
            const data = await response.json();
            if (data.success) {
                showToast("Category deleted successfully!");
                setCategories(prev => prev.filter(c => c.id !== catId));
                if (refreshMenuCatalog) refreshMenuCatalog();
            } else {
                showToast(data.error || "Failed to delete category");
            }
        } catch (err) {
            showToast("Network error");
        }
    };

    const handleOpenAddDish = () => {
        setEditingDish(null);
        const defaultCatId = activeBranchCategories && activeBranchCategories.length > 0 ? String(activeBranchCategories[0].id) : '';
        const itemsInCat = (menuItems || []).filter(m => String(m.category_id) === String(defaultCatId) || String(m.category) === String(defaultCatId));
        const maxOrder = itemsInCat.length > 0 ? Math.max(...itemsInCat.map(m => parseInt(m.display_order || m.displayOrder) || 0)) : 0;

        setDishForm({
            name: '',
            name_mr: '',
            name_hi: '',
            category_id: defaultCatId,
            price: '',
            display_order: maxOrder + 1,
            is_available: true,
            veg: true,
            image_url: ''
        });
        setDishFormOpen(true);
    };

    const handleDishCategoryChange = (catId) => {
        const cleanCatId = String(catId);
        const itemsInCat = (menuItems || []).filter(m => String(m.category_id) === cleanCatId || String(m.category) === cleanCatId);
        const maxOrder = itemsInCat.length > 0 ? Math.max(...itemsInCat.map(m => parseInt(m.display_order || m.displayOrder) || 0)) : 0;
        setDishForm(prev => ({
            ...prev,
            category_id: cleanCatId,
            display_order: maxOrder + 1
        }));
    };

    const handleOpenEditDish = (item) => {
        setEditingDish(item);
        const catId = item.category_id !== undefined && item.category_id !== null ? String(item.category_id) : (item.category !== undefined ? String(item.category) : '');
        setDishForm({
            name: item.name,
            name_mr: item.nameMr || item.name_mr || '',
            name_hi: item.nameHi || item.name_hi || '',
            category_id: catId,
            price: item.price !== undefined ? item.price.toString() : '',
            display_order: item.display_order !== undefined ? item.display_order : (item.displayOrder || 0),
            is_available: item.isAvailable !== undefined ? item.isAvailable : true,
            veg: item.veg !== undefined ? item.veg : (item.is_veg !== undefined ? item.is_veg : true),
            image_url: item.image_url || ''
        });
        setDishFormOpen(true);
    };

    const handleDishFormSubmit = async (e) => {
        e.preventDefault();
        if (!dishForm.name || !dishForm.price || !dishForm.category_id) {
            showToast("Please fill in name, price, and category!");
            return;
        }

        const activeBranch = getActiveBranchId();
        const payload = {
            branch_id: parseInt(editingDish?.branch_id || activeBranch) || 1,
            name: dishForm.name,
            name_mr: dishForm.name_mr || dishForm.name,
            name_hi: dishForm.name_hi || dishForm.name,
            category_id: parseInt(dishForm.category_id),
            price: parseFloat(dishForm.price),
            display_order: parseInt(dishForm.display_order) || 0,
            is_available: dishForm.is_available,
            veg: dishForm.veg !== undefined ? dishForm.veg : true,
            image_url: dishForm.image_url || null
        };

        let success = false;
        if (editingDish) {
            success = await updateMenuItemDetails(editingDish.id, payload);
        } else {
            success = await addMenuItem(payload);
        }

        if (success) {
            setDishFormOpen(false);
        }
    };

    const handleDeleteDish = async (itemId, itemName) => {
        if (window.confirm(`Are you sure you want to delete "${itemName}"? This will delete its associated recipes and cannot be undone.`)) {
            await deleteMenuItem(itemId);
        }
    };

    const handlePriceChange = (itemId, val) => {
        setPriceEditState(prev => ({ ...prev, [itemId]: val }));
    };

    const handleSavePrice = (itemId) => {
        const val = priceEditState[itemId];
        if (val !== undefined && val !== '') {
            updateMenuPrice(itemId, val);
            setPriceEditState(prev => {
                const copy = { ...prev };
                delete copy[itemId];
                return copy;
            });
        }
    };

    const handleWeightChange = (ingId, val) => {
        setWeightEditState(prev => ({ ...prev, [ingId]: val }));
    };

    const handleSaveWeight = async (ingId) => {
        const val = weightEditState[ingId];
        if (val !== undefined && val !== '') {
            await updateRecipeWeight(selectedDishId, ingId, val);
            setWeightEditState(prev => {
                const copy = { ...prev };
                delete copy[ingId];
                return copy;
            });
        }
    };

    const handleDeleteRecipeIng = async (ingId, recipeId) => {
        if (window.confirm('Are you sure you want to remove this ingredient from recipe?')) {
            await deleteRecipeIngredient(selectedDishId, ingId, recipeId);
        }
    };

    const handleAddIngredientToRecipe = async () => {
        if (!selectedDishId) {
            showToast('Please select a dish first.');
            return;
        }
        if (!newIngId) {
            showToast('Please select an ingredient.');
            return;
        }
        const weightVal = parseFloat(newIngWeight);
        if (isNaN(weightVal) || weightVal <= 0) {
            showToast('Please enter a valid ingredient quantity/weight.');
            return;
        }
        await updateRecipeWeight(selectedDishId, newIngId, weightVal);
        setNewIngId('');
        setNewIngWeight('');
    };

    const handleApplyPresetTemplate = async () => {
        const confirmMsg = "WARNING: Applying a template preset will completely reset your store layout, menu categories, dishes, recipes, and inventory items to the selected preset. This cannot be undone. Are you sure you want to proceed?";
        if (!window.confirm(confirmMsg)) return;

        setPresetLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/tenant/menu/apply-template`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Tenant-Subdomain': tenantSubdomain
                },
                body: JSON.stringify({ template: selectedPreset, branch_id: currentUser?.branch_id || 1 })
            });
            const data = await response.json();
            if (data.success) {
                showToast(`Preset template applied successfully!`);
                window.location.reload();
            } else {
                showToast(data.error || 'Failed to apply preset.');
            }
        } catch (error) {
            showToast('Network error.');
        } finally {
            setPresetLoading(false);
        }
    };

    const getDisplayName = (item) => {
        if (!item) return '';
        if (currentLanguage === 'mr') return item.name_mr || item.nameMr || item.name;
        if (currentLanguage === 'hi') return item.name_hi || item.nameHi || item.name;
        return item.name;
    };

    // Selected Dish Recipe details
    const selectedDish = menuItems.find(item => parseInt(item.id) === parseInt(selectedDishId));
    const selectedDishRecipe = Array.isArray(recipes) 
        ? recipes.filter(r => parseInt(r.menu_item_id || r.menuItemId) === parseInt(selectedDishId))
        : (recipes[selectedDishId] || []);

    return (
        <Box sx={{ p: 4, bgcolor: '#f9fafb', minHeight: 'calc(100vh - 70px)' }}>
            <Typography variant="h5" sx={{ fontWeight: 900, color: '#111827', mb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <SettingsIcon sx={{ color: '#008751', fontSize: 32 }} />
                {t('nav-owner-settings')}
            </Typography>

            {/* Top Prominent POS Card Layout Configuration Card */}
            <Card sx={{ borderRadius: 3, border: '2px solid #008751', bgcolor: '#ffffff', p: 2.5, mb: 3, boxShadow: '0 4px 12px rgba(0,135,81,0.08)' }}>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 900, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
                        ⚡ POS Menu Card Layout Selector (कार्ड डिझाईन निवडा)
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                        POS बिलिंग टर्मिनलवर कार्ड्स कसे दिसावेत ते खालील पर्यायांतून सिलेक्ट करा. बदल लगेच बिलिंग स्क्रीनवर अप्लाय होईल!
                    </Typography>
                </Box>

                <Grid container spacing={2}>
                    {/* Option 1: Full Photos Mode */}
                    <Grid item xs={12} sm={4}>
                        <Paper
                            onClick={() => {
                                setPosCardLayout('image');
                                showToast('🖼️ POS वर डिश फोटोसह फुल मोड ऑन केला.');
                            }}
                            elevation={0}
                            sx={{
                                p: 2,
                                borderRadius: 2.5,
                                border: posCardLayout === 'image' ? '2px solid #008751' : '1.5px solid #cbd5e1',
                                bgcolor: posCardLayout === 'image' ? '#f0fdf4' : '#ffffff',
                                cursor: 'pointer',
                                transition: 'all 0.15s ease',
                                '&:hover': { borderColor: '#008751', transform: 'translateY(-2px)' }
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 900, color: posCardLayout === 'image' ? '#008751' : '#0f172a' }}>
                                    🖼️ Mode 1: With Dish Photos
                                </Typography>
                                {posCardLayout === 'image' && <Chip label="ACTIVE" size="small" color="success" sx={{ fontWeight: 900, height: 20 }} />}
                            </Box>
                            <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                                डिशच्या फोटोंसह मूळ विस्तृत कार्ड्स.
                            </Typography>
                        </Paper>
                    </Grid>

                    {/* Option 2: Compact Layout 1 (User Sketch Style) */}
                    <Grid item xs={12} sm={4}>
                        <Paper
                            onClick={() => {
                                setPosCardLayout('compact1');
                                showToast('🟩 Compact Layout 1 (कलरफुल हेडर विदाऊट फोटो मोड) ऑन केला.');
                            }}
                            elevation={0}
                            sx={{
                                p: 2,
                                borderRadius: 2.5,
                                border: posCardLayout === 'compact1' ? '2px solid #ea580c' : '1.5px solid #cbd5e1',
                                bgcolor: posCardLayout === 'compact1' ? '#fff7ed' : '#ffffff',
                                cursor: 'pointer',
                                transition: 'all 0.15s ease',
                                '&:hover': { borderColor: '#ea580c', transform: 'translateY(-2px)' }
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 900, color: posCardLayout === 'compact1' ? '#c2410c' : '#0f172a' }}>
                                    🟩 Compact Layout 1 (Colorful Sketch)
                                </Typography>
                                {posCardLayout === 'compact1' && <Chip label="ACTIVE" size="small" color="warning" sx={{ fontWeight: 900, height: 20 }} />}
                            </Box>
                            <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                                हिरवा/लाल सॉलिड हेडर, व्हाईट बॉटम, रेट आणि गोल + बटण. (User Sketch Style)
                            </Typography>
                        </Paper>
                    </Grid>

                    {/* Option 3: Compact Layout 2 (Clean Sober Slate Style) */}
                    <Grid item xs={12} sm={4}>
                        <Paper
                            onClick={() => {
                                setPosCardLayout('compact2');
                                showToast('⬜ Compact Layout 2 (सोबर ॲक्सेंट विदाऊट फोटो मोड) ऑन केला.');
                            }}
                            elevation={0}
                            sx={{
                                p: 2,
                                borderRadius: 2.5,
                                border: posCardLayout === 'compact2' ? '2px solid #0284c7' : '1.5px solid #cbd5e1',
                                bgcolor: posCardLayout === 'compact2' ? '#f0f9ff' : '#ffffff',
                                cursor: 'pointer',
                                transition: 'all 0.15s ease',
                                '&:hover': { borderColor: '#0284c7', transform: 'translateY(-2px)' }
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 900, color: posCardLayout === 'compact2' ? '#0369a1' : '#0f172a' }}>
                                    ⬜ Compact Layout 2 (Sober Slate)
                                </Typography>
                                {posCardLayout === 'compact2' && <Chip label="ACTIVE" size="small" color="info" sx={{ fontWeight: 900, height: 20 }} />}
                            </Box>
                            <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                                प्युअर व्हाईट बॅकग्राउंड, सोबर डावीपट्टी आणि गोल + बटण. (Minimal Style)
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>
            </Card>

            {/* Tab navigation */}
            <Box sx={{ display: 'flex', gap: 2, mb: 4, borderBottom: '1px solid #e5e7eb', pb: 1 }}>
                <Button
                    variant={activeTab === 'menu' ? 'contained' : 'text'}
                    onClick={() => setActiveTab('menu')}
                    sx={{
                        fontWeight: 800,
                        bgcolor: activeTab === 'menu' ? '#008751' : 'transparent',
                        color: activeTab === 'menu' ? 'white' : '#475569',
                        '&:hover': { bgcolor: activeTab === 'menu' ? '#006f42' : '#f1f5f9' },
                        borderRadius: 2
                    }}
                    startIcon={<RestaurantMenuIcon />}
                >
                    Menu Prices & Recipes
                </Button>
                <Button
                    variant={activeTab === 'preset' ? 'contained' : 'text'}
                    onClick={() => setActiveTab('preset')}
                    sx={{
                        fontWeight: 800,
                        bgcolor: activeTab === 'preset' ? '#008751' : 'transparent',
                        color: activeTab === 'preset' ? 'white' : '#475569',
                        '&:hover': { bgcolor: activeTab === 'preset' ? '#006f42' : '#f1f5f9' },
                        borderRadius: 2
                    }}
                    startIcon={<LayersIcon />}
                >
                    Menu Template presets
                </Button>
                <Button
                    variant={activeTab === 'tables' ? 'contained' : 'text'}
                    onClick={() => setActiveTab('tables')}
                    sx={{
                        fontWeight: 800,
                        bgcolor: activeTab === 'tables' ? '#008751' : 'transparent',
                        color: activeTab === 'tables' ? 'white' : '#475569',
                        '&:hover': { bgcolor: activeTab === 'tables' ? '#006f42' : '#f1f5f9' },
                        borderRadius: 2
                    }}
                    startIcon={<TableBarIcon />}
                >
                    Dining Areas & Tables
                </Button>
            </Box>

            {activeTab === 'menu' && (
                <Grid container spacing={3}>
                    {/* GST / Tax Settings Card */}
                    <Grid item xs={12}>
                        <Card sx={{ borderRadius: 3, border: '1px solid #cbd5e1', bgcolor: '#ffffff', p: 2.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <RestaurantMenuIcon sx={{ color: '#008751', fontSize: 32 }} />
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 900, color: '#0f172a' }}>
                                            🧾 GST / Tax Billing Configuration (जीएसटी कर सेटिंग)
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#64748b' }}>
                                            हॉटेल बिलवर ५% GST लावावा की विना-जीएसटी (0% GST) बिल द्यावे ते इथून ऑन/ऑफ करा.
                                        </Typography>
                                    </Box>
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={gstEnabled}
                                                onChange={(e) => {
                                                    const enabled = e.target.checked;
                                                    setGstEnabled(enabled);
                                                    localStorage.setItem('dinexa_gst_enabled', enabled ? 'true' : 'false');
                                                    localStorage.setItem('dinexa_gst_rate', enabled ? '5' : '0');
                                                    showToast(enabled ? '✅ बिलावर ५% GST ऑन केला!' : '🚫 बिलावरील 5% GST पूर्णपणे बंद केला (0% Tax).');

                                                    // Cloud Database Sync
                                                    try {
                                                        fetch(`${API_BASE_URL}/tenant/settings/gst`, {
                                                            method: 'POST',
                                                            headers: {
                                                                'Content-Type': 'application/json',
                                                                'X-Tenant-Subdomain': tenantSubdomain
                                                            },
                                                            body: JSON.stringify({ gst_enabled: enabled ? 1 : 0 })
                                                        });
                                                    } catch (err) {
                                                        console.error('GST cloud sync failed:', err);
                                                    }
                                                }}
                                                color="success"
                                            />
                                        }
                                        label={
                                            <Typography sx={{ fontWeight: 900, color: gstEnabled ? '#008751' : '#dc2626', fontSize: '0.95rem' }}>
                                                {gstEnabled ? '🟢 5% GST ON (बिलावर 5% जीएसटी चालू आहे)' : '🔴 GST OFF (0% GST - विना जीएसटी बिल चालू आहे)'}
                                            </Typography>
                                        }
                                    />
                                </Box>
                            </Box>
                        </Card>
                    </Grid>

                    {/* Business Day Cut-off Time Configuration Card */}
                    <Grid item xs={12}>
                        <Card sx={{ borderRadius: 3, border: '1px solid #cbd5e1', bgcolor: '#ffffff', p: 2.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <SettingsIcon sx={{ color: '#ea580c', fontSize: 32 }} />
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 900, color: '#0f172a' }}>
                                            🌙 व्यवसाय दिवस कट-ऑफ वेळ (Business Day Cut-off Hour)
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#64748b' }}>
                                            रात्री १२ नंतर (उदा. पहाटे ०४:०० पर्यंत) झालेली लेट-नाईट बिले व क्लोजिंग कॅश मागील तारखेच्या व्यवसायात जमा होतील.
                                        </Typography>
                                    </Box>
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <FormControl size="small" sx={{ minWidth: 260 }}>
                                        <InputLabel id="cutoff-label" sx={{ fontWeight: 700 }}>कट-ऑफ वेळ (Cut-Off Time)</InputLabel>
                                        <Select
                                            labelId="cutoff-label"
                                            value={dayCutoffTime}
                                            label="कट-ऑफ वेळ (Cut-Off Time)"
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                setDayCutoffTime(val);
                                                localStorage.setItem('dinexa_day_cutoff_time', val);
                                                showToast(`✅ व्यवसाय दिवस कट-ऑफ वेळ ${val} सेट केली!`);

                                                try {
                                                    fetch(`${API_BASE_URL}/tenant/settings/gst`, {
                                                        method: 'POST',
                                                        headers: {
                                                            'Content-Type': 'application/json',
                                                            'X-Tenant-Subdomain': tenantSubdomain
                                                        },
                                                        body: JSON.stringify({ 
                                                            branch_id: currentUser?.branch_id || 1,
                                                            day_cutoff_time: val 
                                                        })
                                                    });
                                                } catch (err) {
                                                    console.error('Cutoff cloud sync failed:', err);
                                                }
                                            }}
                                            sx={{ fontWeight: 800, bgcolor: '#f8fafc', borderRadius: 2 }}
                                        >
                                            <MenuItem value="00:00">रात्री १२:०० (12:00 AM Midnight - Standard)</MenuItem>
                                            <MenuItem value="01:00">पहाटे ०१:०० (01:00 AM Late Night)</MenuItem>
                                            <MenuItem value="02:00">पहाटे ०२:०० (02:00 AM Late Night)</MenuItem>
                                            <MenuItem value="03:00">पहाटे ०३:०० (03:00 AM Late Night)</MenuItem>
                                            <MenuItem value="04:00">पहाटे ०४:०० (04:00 AM Recommended)</MenuItem>
                                            <MenuItem value="05:00">पहाटे ०५:०० (05:00 AM Early Morning)</MenuItem>
                                            <MenuItem value="06:00">सकाळी ०६:०० (06:00 AM Morning)</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Box>
                            </Box>
                        </Card>
                    </Grid>

                    <Grid item xs={12}>
                        <Card sx={{ borderRadius: 3, border: '1px solid #cbd5e1', bgcolor: '#ffffff', p: 2 }}>
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="h6" sx={{ fontWeight: 900, color: '#0f172a' }}>
                                    ⚡ POS Display Performance & Card Layout Settings (कार्ड लेआऊट निवडा)
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                                    POS बिलिंग काउंटरवर डिशेस कशा दिसाव्यात ते निवडा. विना-फोटो मोडमुळे टचस्क्रीन टॅब्लेट व पीसीवर ऑर्डर्स पंच करण्याचा वेग प्रचंड वाढतो.
                                </Typography>
                            </Box>

                            <Grid container spacing={2}>
                                {/* Option 1: Full Photos Mode */}
                                <Grid item xs={12} sm={4}>
                                    <Paper
                                        onClick={() => {
                                            setPosCardLayout('image');
                                            showToast('🖼️ POS वर डिश फोटोसह फुल मोड ऑन केला.');
                                        }}
                                        elevation={0}
                                        sx={{
                                            p: 2,
                                            borderRadius: 2.5,
                                            border: posCardLayout === 'image' ? '2px solid #008751' : '1px solid #cbd5e1',
                                            bgcolor: posCardLayout === 'image' ? '#f0fdf4' : '#ffffff',
                                            cursor: 'pointer',
                                            transition: 'all 0.15s ease',
                                            '&:hover': { borderColor: '#008751' }
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: posCardLayout === 'image' ? '#008751' : '#0f172a' }}>
                                                🖼️ Mode 1: With Dish Photos
                                            </Typography>
                                            {posCardLayout === 'image' && <Chip label="ACTIVE" size="small" color="success" sx={{ fontWeight: 900, height: 20 }} />}
                                        </Box>
                                        <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                                            डिशच्या फोटोंसह मुख्य मेनू दिसेल.
                                        </Typography>
                                    </Paper>
                                </Grid>

                                {/* Option 2: Compact Layout 1 (User Sketch Style) */}
                                <Grid item xs={12} sm={4}>
                                    <Paper
                                        onClick={() => {
                                            setPosCardLayout('compact1');
                                            showToast('🟩 Compact Layout 1 (कलरफुल हेडर विदाऊट फोटो मोड) ऑन केला.');
                                        }}
                                        elevation={0}
                                        sx={{
                                            p: 2,
                                            borderRadius: 2.5,
                                            border: posCardLayout === 'compact1' ? '2px solid #ea580c' : '1px solid #cbd5e1',
                                            bgcolor: posCardLayout === 'compact1' ? '#fff7ed' : '#ffffff',
                                            cursor: 'pointer',
                                            transition: 'all 0.15s ease',
                                            '&:hover': { borderColor: '#ea580c' }
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: posCardLayout === 'compact1' ? '#c2410c' : '#0f172a' }}>
                                                🟩 Compact Layout 1 (Colorful)
                                            </Typography>
                                            {posCardLayout === 'compact1' && <Chip label="ACTIVE" size="small" color="warning" sx={{ fontWeight: 900, height: 20 }} />}
                                        </Box>
                                        <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                                            Veg (हिरवा) व Non-Veg (लाल) हेडर आणि खाली दर + बटण. (High Contrast Fast Billing Mode)
                                        </Typography>
                                    </Paper>
                                </Grid>

                                {/* Option 3: Compact Layout 2 (Clean Sober Slate Style) */}
                                <Grid item xs={12} sm={4}>
                                    <Paper
                                        onClick={() => {
                                            setPosCardLayout('compact2');
                                            showToast('⬜ Compact Layout 2 (सोबर ॲक्सेंट विदाऊट फोटो मोड) ऑन केला.');
                                        }}
                                        elevation={0}
                                        sx={{
                                            p: 2,
                                            borderRadius: 2.5,
                                            border: posCardLayout === 'compact2' ? '2px solid #0284c7' : '1px solid #cbd5e1',
                                            bgcolor: posCardLayout === 'compact2' ? '#f0f9ff' : '#ffffff',
                                            cursor: 'pointer',
                                            transition: 'all 0.15s ease',
                                            '&:hover': { borderColor: '#0284c7' }
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: posCardLayout === 'compact2' ? '#0369a1' : '#0f172a' }}>
                                                ⬜ Compact Layout 2 (Sober)
                                            </Typography>
                                            {posCardLayout === 'compact2' && <Chip label="ACTIVE" size="small" color="info" sx={{ fontWeight: 900, height: 20 }} />}
                                        </Box>
                                        <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                                            क्लीन व्हाईट बॅकग्राउंड, सोबर डावीपट्टी आणि सॉफ्ट बॉर्डर्स.
                                        </Typography>
                                    </Paper>
                                </Grid>
                            </Grid>
                        </Card>
                    </Grid>

                    {/* 1. Menu Price Editor */}
                    <Grid item xs={12} md={menuViewMode === 'excel' ? 12 : 7}>
                        <Card sx={{ borderRadius: 3, border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#111827', m: 0 }}>
                                            Menu Prices Configurator (मेन्यू दर व्यवस्थापन)
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mt: 0.3 }}>
                                            {menuViewMode === 'excel' 
                                                ? '📊 एक्सेल स्प्रेडशीट मोड: थेट सेलमध्ये टाईप करा, Tab/Enter ने नेव्हिगेट करा आणि रो-बाय-रो किंवा एकत्र सेव्ह करा.'
                                                : 'Update prices of items instantly. Changes reflect immediately in the active billing registers and customer receipts.'}
                                        </Typography>
                                    </Box>

                                    {/* Action Buttons */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                        {/* Dual View Mode Switcher Pill */}
                                        <Box sx={{ display: 'flex', bgcolor: '#f1f5f9', p: 0.5, borderRadius: 2, border: '1px solid #cbd5e1' }}>
                                            <Button
                                                size="small"
                                                variant={menuViewMode === 'classic' ? 'contained' : 'text'}
                                                onClick={() => handleSetMenuViewMode('classic')}
                                                startIcon={<ListAltIcon sx={{ fontSize: '17px !important' }} />}
                                                sx={{
                                                    fontWeight: 800,
                                                    fontSize: '0.75rem',
                                                    borderRadius: 1.5,
                                                    textTransform: 'none',
                                                    py: 0.4,
                                                    px: 1.2,
                                                    bgcolor: menuViewMode === 'classic' ? '#008751' : 'transparent',
                                                    color: menuViewMode === 'classic' ? '#ffffff' : '#64748b',
                                                    '&:hover': { bgcolor: menuViewMode === 'classic' ? '#007043' : '#e2e8f0' }
                                                }}
                                            >
                                                📋 Classic List
                                            </Button>
                                            <Button
                                                size="small"
                                                variant={menuViewMode === 'excel' ? 'contained' : 'text'}
                                                onClick={() => handleSetMenuViewMode('excel')}
                                                startIcon={<TableChartIcon sx={{ fontSize: '17px !important' }} />}
                                                sx={{
                                                    fontWeight: 800,
                                                    fontSize: '0.75rem',
                                                    borderRadius: 1.5,
                                                    textTransform: 'none',
                                                    py: 0.4,
                                                    px: 1.2,
                                                    bgcolor: menuViewMode === 'excel' ? '#0284c7' : 'transparent',
                                                    color: menuViewMode === 'excel' ? '#ffffff' : '#64748b',
                                                    '&:hover': { bgcolor: menuViewMode === 'excel' ? '#0369a1' : '#e2e8f0' }
                                                }}
                                            >
                                                📊 Excel Grid
                                            </Button>
                                        </Box>


                                        {isCsvFeatureEnabled && (
                                            <Button
                                                variant="outlined"
                                                color="secondary"
                                                component="label"
                                                size="small"
                                                startIcon={<FileUploadIcon sx={{ fontSize: '16px !important' }} />}
                                                sx={{ fontWeight: 800, borderRadius: 2, textTransform: 'none', py: 0.5 }}
                                            >
                                                Import CSV
                                                <input
                                                    type="file"
                                                    accept=".csv"
                                                    hidden
                                                    onChange={handleCsvFileSelect}
                                                />
                                            </Button>
                                        )}
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={handleOpenAddCategory}
                                            sx={{ color: '#008751', borderColor: '#008751', '&:hover': { borderColor: '#006f42', bgcolor: '#f0fdf4' }, fontWeight: 800, borderRadius: 2, textTransform: 'none', py: 0.5 }}
                                        >
                                            Manage Categories
                                        </Button>

                                        {menuViewMode === 'classic' ? (
                                            <Button
                                                variant="contained"
                                                size="small"
                                                startIcon={<AddIcon sx={{ fontSize: '16px !important' }} />}
                                                onClick={handleOpenAddDish}
                                                sx={{ bgcolor: '#008751', '&:hover': { bgcolor: '#006f42' }, fontWeight: 800, borderRadius: 2, textTransform: 'none', py: 0.5 }}
                                            >
                                                Add Dish
                                            </Button>
                                        ) : (
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={<AddIcon sx={{ fontSize: '16px !important' }} />}
                                                    onClick={() => handleAddGridRow(1)}
                                                    sx={{ fontWeight: 800, borderRadius: 2, textTransform: 'none', py: 0.5, borderColor: '#0284c7', color: '#0284c7' }}
                                                >
                                                    + नवीन पंक्ती (+ Row)
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={<PlaylistAddIcon sx={{ fontSize: '16px !important' }} />}
                                                    onClick={() => handleAddGridRow(5)}
                                                    sx={{ fontWeight: 800, borderRadius: 2, textTransform: 'none', py: 0.5, borderColor: '#0284c7', color: '#0284c7' }}
                                                >
                                                    + 5 Rows
                                                </Button>
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    startIcon={<SaveIcon sx={{ fontSize: '16px !important' }} />}
                                                    onClick={handleSaveAllGridChanges}
                                                    disabled={batchSaving || Object.keys(dirtyRowIds).filter(k => dirtyRowIds[k]).length === 0}
                                                    sx={{
                                                        bgcolor: '#008751',
                                                        '&:hover': { bgcolor: '#006f42' },
                                                        fontWeight: 900,
                                                        borderRadius: 2,
                                                        textTransform: 'none',
                                                        py: 0.5,
                                                        px: 1.5,
                                                        boxShadow: '0 2px 6px rgba(0,135,81,0.3)'
                                                    }}
                                                >
                                                    {batchSaving ? 'सेव्ह होत आहे...' : `💾 सर्व बदल सेव्ह करा ${Object.keys(dirtyRowIds).filter(k => dirtyRowIds[k]).length > 0 ? `(${Object.keys(dirtyRowIds).filter(k => dirtyRowIds[k]).length})` : ''}`}
                                                </Button>
                                            </Box>
                                        )}
                                    </Box>
                                </Box>

                                {/* Unsaved changes banner in Excel Mode */}
                                {menuViewMode === 'excel' && Object.keys(dirtyRowIds).filter(k => dirtyRowIds[k]).length > 0 && (
                                    <Alert 
                                        severity="warning" 
                                        icon={<AutoAwesomeIcon />}
                                        action={
                                            <Button 
                                                color="inherit" 
                                                size="small" 
                                                onClick={handleSaveAllGridChanges}
                                                sx={{ fontWeight: 900, textTransform: 'none', textDecoration: 'underline' }}
                                            >
                                                आत्ताच सेव्ह करा (Save All)
                                            </Button>
                                        }
                                        sx={{ mb: 2, borderRadius: 2, fontWeight: 700, fontSize: '0.85rem' }}
                                    >
                                        ⚠️ {Object.keys(dirtyRowIds).filter(k => dirtyRowIds[k]).length} नोंदींमध्ये बदल झाले आहेत. तुम्ही उजवीकडील प्रत्येक ओळीसमोरील <strong>'Save'</strong> बटण किंवा वरील हिरवे <strong>'सर्व बदल सेव्ह करा'</strong> बटण वापरू शकता.
                                    </Alert>
                                )}

                                {/* Menu Items Instant Search Bar & Category Tabs */}
                                <Box sx={{ mb: 2 }}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        placeholder="शोधा (Search dish by name or category, e.g. Paneer, Veg Thali, Starters...)"
                                        value={menuSearchQuery}
                                        onChange={(e) => setMenuSearchQuery(e.target.value)}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon sx={{ color: '#008751' }} />
                                                </InputAdornment>
                                            ),
                                            endAdornment: menuSearchQuery && (
                                                <InputAdornment position="end">
                                                    <IconButton size="small" onClick={() => setMenuSearchQuery('')}>
                                                        <CloseIcon fontSize="small" />
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                        sx={{ bgcolor: '#ffffff', borderRadius: 2 }}
                                    />
                                </Box>

                                {/* Category Filter Chips Bar */}
                                <Box sx={{ display: 'flex', gap: 1, mb: 2.5, overflowX: 'auto', pb: 0.5, alignItems: 'center' }}>
                                    <Chip
                                        label="सर्व कॅटेगिरी (All)"
                                        onClick={() => { setSelectedCategoryTab('all'); setMenuPage(0); }}
                                        variant={selectedCategoryTab === 'all' ? 'filled' : 'outlined'}
                                        sx={{
                                            fontWeight: 800, fontSize: '0.8rem', height: 32,
                                            bgcolor: selectedCategoryTab === 'all' ? '#008751' : '#ffffff',
                                            color: selectedCategoryTab === 'all' ? '#ffffff' : '#4b5563',
                                            borderColor: '#d1d5db'
                                        }}
                                    />
                                    {activeBranchCategories && activeBranchCategories.map(cat => (
                                        <Chip
                                            key={cat.id}
                                            label={currentLanguage === 'mr' ? (cat.name_mr || cat.name) : cat.name}
                                            onClick={() => { setSelectedCategoryTab(cat.id); setMenuPage(0); }}
                                            variant={selectedCategoryTab == cat.id ? 'filled' : 'outlined'}
                                            sx={{
                                                fontWeight: 800, fontSize: '0.8rem', height: 32,
                                                bgcolor: selectedCategoryTab == cat.id ? '#008751' : '#ffffff',
                                                color: selectedCategoryTab == cat.id ? '#ffffff' : '#4b5563',
                                                borderColor: '#d1d5db'
                                            }}
                                        />
                                    ))}
                                </Box>

                                {menuViewMode === 'excel' ? (
                                    /* =========================================================
                                       EXCEL SPREADSHEET GRID MODE (Fast In-Grid Typing & Row Save)
                                       ========================================================= */
                                    <Box>
                                        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #cbd5e1', borderRadius: 2, maxHeight: 540, overflowY: 'auto' }}>
                                            <Table size="small" stickyHeader sx={{ minWidth: 860, '& .MuiTableCell-root': { py: 0.8, px: 1 } }}>
                                                <TableHead sx={{ bgcolor: '#f8fafc' }}>
                                                    <TableRow>
                                                        <TableCell 
                                                            sx={{ fontWeight: 900, width: 75, bgcolor: '#f1f5f9', cursor: 'pointer', userSelect: 'none', '&:hover': { bgcolor: '#e2e8f0' } }} 
                                                            align="center"
                                                            onClick={handleToggleOrderSort}
                                                            title="क्रम सॉर्ट करा (चढता/उतरता क्रम - Click to Sort Asc/Desc)"
                                                        >
                                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.3 }}>
                                                                <span># (क्रम)</span>
                                                                <span style={{ fontSize: '11px', color: '#008751', fontWeight: 900 }}>
                                                                    {gridSortDirection === 'asc' ? '▲' : '▼'}
                                                                </span>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell sx={{ fontWeight: 900, minWidth: 180, bgcolor: '#f1f5f9' }}>Dish Name (English) *</TableCell>
                                                        <TableCell sx={{ fontWeight: 900, minWidth: 160, bgcolor: '#f1f5f9' }}>मराठी नाव (Marathi Name)</TableCell>
                                                        <TableCell sx={{ fontWeight: 900, minWidth: 160, bgcolor: '#f1f5f9' }}>हिन्दी नाव (Hindi Name)</TableCell>
                                                        <TableCell sx={{ fontWeight: 900, minWidth: 150, bgcolor: '#f1f5f9' }}>Category *</TableCell>
                                                        <TableCell sx={{ fontWeight: 900, width: 105, bgcolor: '#f1f5f9' }} align="right">Price (₹) *</TableCell>
                                                        <TableCell sx={{ fontWeight: 900, width: 100, bgcolor: '#f1f5f9' }} align="center">Type</TableCell>
                                                        <TableCell sx={{ fontWeight: 900, width: 80, bgcolor: '#f1f5f9' }} align="center">Active</TableCell>
                                                        <TableCell sx={{ fontWeight: 900, width: 135, bgcolor: '#f1f5f9' }} align="center">Action (थेट सेव्ह)</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {filteredGridRows.length === 0 ? (
                                                        <TableRow>
                                                            <TableCell colSpan={9} align="center" sx={{ py: 4, color: '#94a3b8', fontWeight: 700 }}>
                                                                कोणतेही मेन्यू आयटम सापडले नाहीत. नवीन डिश जोडण्यासाठी वरील <strong>"+ नवीन पंक्ती (+ Row)"</strong> वर क्लिक करा.
                                                            </TableCell>
                                                        </TableRow>
                                                    ) : (
                                                        filteredGridRows.map((row, rIdx) => (
                                                            <GridRowItem
                                                                key={row.id || row._tempId}
                                                                row={row}
                                                                rIdx={rIdx}
                                                                categories={activeBranchCategories}
                                                                currentLanguage={currentLanguage}
                                                                isDirty={!!dirtyRowIds[row.id || row._tempId]}
                                                                saveStatus={rowSavingState[row.id || row._tempId]}
                                                                onCellChange={handleGridCellChange}
                                                                onSaveRow={handleSaveSingleGridRow}
                                                                onDeleteRow={handleDeleteGridRow}
                                                                onAddRowBelow={() => handleAddGridRow(1)}
                                                            />
                                                        ))
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>

                                        {/* Bottom Action Footer for Grid */}
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, pt: 1.5, borderTop: '1px solid #e2e8f0', flexWrap: 'wrap', gap: 1 }}>
                                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={<AddIcon />}
                                                    onClick={() => handleAddGridRow(1)}
                                                    sx={{ fontWeight: 800, borderRadius: 2, textTransform: 'none' }}
                                                >
                                                    + नवीन पंक्ती (+ Row)
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={<PlaylistAddIcon />}
                                                    onClick={() => handleAddGridRow(5)}
                                                    sx={{ fontWeight: 800, borderRadius: 2, textTransform: 'none' }}
                                                >
                                                    + 5 Rows
                                                </Button>
                                                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, ml: 1 }}>
                                                    एकूण नोंदी: {filteredGridRows.length} | बदल बाकी: {Object.keys(dirtyRowIds).filter(k => dirtyRowIds[k]).length}
                                                </Typography>
                                            </Box>

                                            <Button
                                                variant="contained"
                                                startIcon={<SaveIcon />}
                                                onClick={handleSaveAllGridChanges}
                                                disabled={batchSaving || Object.keys(dirtyRowIds).filter(k => dirtyRowIds[k]).length === 0}
                                                sx={{
                                                    bgcolor: '#008751',
                                                    '&:hover': { bgcolor: '#006f42' },
                                                    fontWeight: 900,
                                                    borderRadius: 2,
                                                    textTransform: 'none',
                                                    px: 2.5
                                                }}
                                            >
                                                {batchSaving ? 'सेव्ह होत आहे...' : `💾 सर्व बदल सेव्ह करा ${Object.keys(dirtyRowIds).filter(k => dirtyRowIds[k]).length > 0 ? `(${Object.keys(dirtyRowIds).filter(k => dirtyRowIds[k]).length})` : ''}`}
                                            </Button>
                                        </Box>
                                    </Box>
                                ) : (
                                    /* =========================================================
                                       CLASSIC LIST MODE (Standard Modal & Pagination View)
                                       ========================================================= */
                                    <Box>
                                        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e5e7eb', borderRadius: 2, maxHeight: 450, overflowY: 'auto' }}>
                                            <Table size="small" stickyHeader>
                                                <TableHead sx={{ bgcolor: '#f9fafb' }}>
                                                    <TableRow>
                                                        <TableCell sx={{ fontWeight: 800, width: 60 }} align="center">क्रम (#)</TableCell>
                                                        <TableCell sx={{ fontWeight: 800 }}>Dish Name</TableCell>
                                                        <TableCell sx={{ fontWeight: 800 }}>Category</TableCell>
                                                        <TableCell sx={{ fontWeight: 800 }} align="right">Current Price</TableCell>
                                                        <TableCell sx={{ fontWeight: 800 }} align="center">New Price (₹)</TableCell>
                                                        <TableCell sx={{ fontWeight: 800 }} align="center">Action & Reorder</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {paginatedMenuItems.length === 0 ? (
                                                        <TableRow>
                                                            <TableCell colSpan={6} align="center" sx={{ py: 3, color: '#9ca3af', fontWeight: 600 }}>
                                                                "{menuSearchQuery}" या नावाने कोणताही पदार्थ किंवा कॅटेगरी सापडली नाही.
                                                            </TableCell>
                                                        </TableRow>
                                                    ) : (
                                                        paginatedMenuItems.map((item, idx) => {
                                                            const editVal = priceEditState[item.id] !== undefined ? priceEditState[item.id] : '';
                                                            const dispOrderNum = item.display_order !== undefined ? item.display_order : (item.displayOrder !== undefined ? item.displayOrder : (idx + 1));
                                                            return (
                                                                <TableRow key={item.id} hover sx={{ '&:hover': { bgcolor: '#f9fafb' } }}>
                                                                    <TableCell align="center">
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                                                            <TextField
                                                                                size="small"
                                                                                type="number"
                                                                                value={orderEditState[item.id] !== undefined ? orderEditState[item.id] : dispOrderNum}
                                                                                onChange={(e) => handleOrderChange(item.id, e.target.value)}
                                                                                onKeyDown={(e) => {
                                                                                    if (e.key === 'Enter') handleSaveOrder(item);
                                                                                }}
                                                                                title="अनुक्रम थेट एडिट करा आणि Enter दाबा किंवा सेव्ह करा"
                                                                                sx={{
                                                                                    width: 60,
                                                                                    '& input': {
                                                                                        py: 0.4,
                                                                                        px: 0.5,
                                                                                        textAlign: 'center',
                                                                                        fontWeight: 900,
                                                                                        fontSize: '0.85rem',
                                                                                        color: orderEditState[item.id] !== undefined ? '#d97706' : '#008751',
                                                                                        bgcolor: orderEditState[item.id] !== undefined ? '#fffbe6' : '#f8fafc',
                                                                                        borderRadius: 1
                                                                                    }
                                                                                }}
                                                                            />
                                                                            {orderEditState[item.id] !== undefined && (
                                                                                <IconButton
                                                                                    size="small"
                                                                                    color="success"
                                                                                    title="नवीन अनुक्रम सेव्ह करा"
                                                                                    onClick={() => handleSaveOrder(item)}
                                                                                    sx={{ p: 0.3, bgcolor: '#d1fae5', color: '#047857' }}
                                                                                >
                                                                                    <CheckIcon sx={{ fontSize: 14 }} />
                                                                                </IconButton>
                                                                            )}
                                                                        </Box>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                                            {getDisplayName(item)}
                                                                        </Typography>
                                                                        <Typography variant="caption" sx={{ color: '#9ca3af', display: 'block' }}>
                                                                            ID: {item.id}
                                                                        </Typography>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Chip
                                                                            label={item.category ? item.category.toUpperCase() : ''}
                                                                            size="small"
                                                                            variant="outlined"
                                                                            sx={{ fontWeight: 800, fontSize: '0.6rem', height: 18 }}
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell align="right" sx={{ fontWeight: 800, color: '#008751' }}>
                                                                        ₹{(Number(item.price) || 0).toFixed(2)}
                                                                    </TableCell>
                                                                    <TableCell align="center">
                                                                        <TextField
                                                                            size="small"
                                                                            type="number"
                                                                            placeholder="0.00"
                                                                            value={editVal}
                                                                            onChange={(e) => handlePriceChange(item.id, e.target.value)}
                                                                            sx={{ width: 90, '& input': { py: 0.5, textAlign: 'right', fontWeight: 700 } }}
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell align="center">
                                                                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', alignItems: 'center' }}>
                                                                            <IconButton
                                                                                size="small"
                                                                                title="वर हलवा (Move Up)"
                                                                                onClick={() => handleMoveDishOrder(item, 'up')}
                                                                                sx={{ p: 0.5, color: '#008751', border: '1px solid #d1fae5', bgcolor: '#ecfdf5', '&:hover': { bgcolor: '#d1fae5' } }}
                                                                            >
                                                                                <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                                                                            </IconButton>
                                                                            <IconButton
                                                                                size="small"
                                                                                title="खाली हलवा (Move Down)"
                                                                                onClick={() => handleMoveDishOrder(item, 'down')}
                                                                                sx={{ p: 0.5, color: '#008751', border: '1px solid #d1fae5', bgcolor: '#ecfdf5', '&:hover': { bgcolor: '#d1fae5' } }}
                                                                            >
                                                                                <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                                                                            </IconButton>
                                                                            <Button
                                                                                size="small"
                                                                                variant="contained"
                                                                                color="primary"
                                                                                disabled={editVal === ''}
                                                                                onClick={() => handleSavePrice(item.id)}
                                                                                sx={{ minWidth: 32, p: 0.5, bgcolor: '#008751', '&:hover': { bgcolor: '#006f42' } }}
                                                                            >
                                                                                <SaveIcon sx={{ fontSize: 16 }} />
                                                                            </Button>
                                                                            <IconButton
                                                                                size="small"
                                                                                color="primary"
                                                                                onClick={() => handleOpenEditDish(item)}
                                                                                sx={{ p: 0.5 }}
                                                                            >
                                                                                <EditIcon sx={{ fontSize: 16 }} />
                                                                            </IconButton>
                                                                            <IconButton
                                                                                size="small"
                                                                                color="error"
                                                                                onClick={() => handleDeleteDish(item.id, item.name)}
                                                                                sx={{ p: 0.5 }}
                                                                            >
                                                                                <DeleteIcon sx={{ fontSize: 16 }} />
                                                                            </IconButton>
                                                                        </Box>
                                                                    </TableCell>
                                                                </TableRow>
                                                            );
                                                        })
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                        <TablePagination
                                            component="div"
                                            count={filteredMenuItems.length}
                                            page={menuPage}
                                            onPageChange={(e, newPage) => setMenuPage(newPage)}
                                            rowsPerPage={menuRowsPerPage}
                                            onRowsPerPageChange={(e) => {
                                                setMenuRowsPerPage(parseInt(e.target.value, 10));
                                                setMenuPage(0);
                                            }}
                                            rowsPerPageOptions={[15, 25, 50, 100]}
                                            sx={{ borderTop: '1px solid #e5e7eb' }}
                                        />
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* 2. Recipe Ingredients Weight Editor & System Settings */}
                    <Grid item xs={12} md={5}>
                        {/* GST Billing Settings Card */}
                        <Card sx={{ borderRadius: 3, border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', mb: 3 }}>
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Box sx={{ pr: 2 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#111827', mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                            🧾 GST Tax Billing (जीएसटी बिलिंग सेटिंग)
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500, fontSize: '0.85rem' }}>
                                            बिलावर ५% जीएसटी (CGST 2.5% + SGST 2.5%) लागू करायचा असल्यास हे ऑन ठेवा. जीएसटी नको असल्यास हे ऑफ ठेवा.
                                        </Typography>
                                    </Box>
                                    <Switch
                                        checked={localStorage.getItem('dinexa_gst_enabled') === 'true' || localStorage.getItem('dinexa_gst_enabled') === '1'}
                                        onChange={async (e) => {
                                            const val = e.target.checked;
                                            localStorage.setItem('dinexa_gst_enabled', val ? 'true' : 'false');
                                            try {
                                                const activeSub = tenantSubdomain || currentUser?.subdomain || localStorage.getItem('dinexa_active_tenant_subdomain');
                                                const res = await fetch(`${API_BASE_URL}/tenant/settings/gst?tenant=${encodeURIComponent(activeSub)}`, {
                                                    method: 'POST',
                                                    headers: {
                                                        'Content-Type': 'application/json',
                                                        'X-Tenant-Subdomain': activeSub
                                                    },
                                                    body: JSON.stringify({ gst_enabled: val })
                                                });
                                                const data = await res.json();
                                                if (data.success) {
                                                    showToast(val ? '🧾 GST बिलिंग ऑन केले आणि डेटाबेसवर सेव्ह झाले!' : '🚫 GST बिलिंग बंद केले आणि डेटाबेसवर सेव्ह झाले!');
                                                } else {
                                                    showToast(data.error || 'डेटाबेसवर GST सेटिंग अपडेट होऊ शकली नाही.');
                                                }
                                            } catch (err) {
                                                console.error('Failed to sync GST setting to server:', err);
                                            }
                                        }}
                                        color="success"
                                    />
                                </Box>
                            </CardContent>
                        </Card>

                        {/* Realtime Cloud Sync Settings Card */}
                        <Card sx={{ borderRadius: 3, border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', mb: 3 }}>
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Box sx={{ pr: 2 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#111827', mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                            ⚡ Realtime Cloud Sync (क्लाऊड सिंक मोड्स)
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500, fontSize: '0.85rem' }}>
                                            कॅप्टन ॲप किंवा मल्टी-डिव्हाईस (मल्टीपल वेटर) वापरत असल्यास हे ऑन ठेवा. **१ टॅब काउंटरसाठी हे ऑफ ठेवा** जेणेकरून ॲप अत्यंत फास्ट आणि विना-लॅग चालेल.
                                        </Typography>
                                    </Box>
                                    <Switch
                                        checked={cloudSyncEnabled}
                                        onChange={(e) => toggleCloudSync(e.target.checked)}
                                        color="success"
                                    />
                                </Box>
                                <Chip 
                                    label={cloudSyncEnabled ? '⚡ ON: Table Floor Plan Auto-Sync Active (Multi-Captain Mode)' : '💤 OFF: Single Tablet Ultra-Fast Mode (0% Background Polling)'} 
                                    size="small"
                                    sx={{ 
                                        mt: 1.5, 
                                        bgcolor: cloudSyncEnabled ? '#ecfdf5' : '#f1f5f9', 
                                        color: cloudSyncEnabled ? '#047857' : '#475569', 
                                        fontWeight: 800, 
                                        border: '1px solid',
                                        borderColor: cloudSyncEnabled ? '#a7f3d0' : '#cbd5e1'
                                    }} 
                                />
                            </CardContent>
                        </Card>

                        <Card sx={{ borderRadius: 3, border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ fontWeight: 800, color: '#111827', mb: 2 }}>
                                    Recipe Ingredients Config (रेसिपी साहित्य प्रमाण)
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#6b7280', mb: 3 }}>
                                    Update raw ingredient weight consumption settings. This updates sales outward reconciliation values.
                                </Typography>

                                {/* Select Dish */}
                                <FormControl fullWidth sx={{ mb: 3 }}>
                                    <InputLabel>Select Menu Item (Dish)</InputLabel>
                                    <Select
                                        value={selectedDishId}
                                        label="Select Menu Item (Dish)"
                                        onChange={(e) => {
                                            setSelectedDishId(e.target.value);
                                            setWeightEditState({});
                                        }}
                                        sx={{ borderRadius: 2 }}
                                    >
                                        {menuItems.map(item => (
                                            <MenuItem key={item.id} value={item.id}>
                                                {getDisplayName(item)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                {selectedDish && (
                                    <Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, p: 1.5, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <RestaurantMenuIcon sx={{ color: '#008751' }} />
                                                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#166534' }}>
                                                    Ingredients List for: {getDisplayName(selectedDish)} (₹{selectedDish.price})
                                                </Typography>
                                            </Box>
                                            <Chip label={`${selectedDishRecipe.length} Items`} size="small" color="success" sx={{ fontWeight: 800 }} />
                                        </Box>

                                        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e5e7eb', borderRadius: 2 }}>
                                            <Table size="small">
                                                <TableHead sx={{ bgcolor: '#f9fafb' }}>
                                                    <TableRow>
                                                        <TableCell sx={{ fontWeight: 800 }}>Ingredient (कच्चा माल)</TableCell>
                                                        <TableCell sx={{ fontWeight: 800 }} align="right">Qty / Plate</TableCell>
                                                        <TableCell sx={{ fontWeight: 800 }} align="center">Edit Qty</TableCell>
                                                        <TableCell sx={{ fontWeight: 800 }} align="center">Action</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {selectedDishRecipe.length === 0 ? (
                                                        <TableRow>
                                                            <TableCell colSpan={4} align="center" sx={{ py: 3, color: '#9ca3af', fontWeight: 600 }}>
                                                                No ingredients configured for this item. Add raw materials below!
                                                            </TableCell>
                                                        </TableRow>
                                                    ) : (
                                                        selectedDishRecipe.map((ri) => {
                                                            const ingId = parseInt(ri.ingredient_id || ri.ingId);
                                                            const ingItem = (inventory || []).find(i => parseInt(i.id) === ingId);
                                                            const editVal = weightEditState[ingId] !== undefined ? weightEditState[ingId] : '';
                                                            return (
                                                                <TableRow key={ri.id || `${ri.menu_item_id}-${ingId}`}>
                                                                    <TableCell>
                                                                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                                            {ingItem ? getDisplayName(ingItem) : (ri.ingredient_name || `Ingredient #${ingId}`)}
                                                                        </Typography>
                                                                        <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                                                                            Unit: {ingItem?.unit || ri.ingredient_unit || 'kg'}
                                                                        </Typography>
                                                                    </TableCell>
                                                                    <TableCell align="right" sx={{ fontWeight: 800 }}>
                                                                        {ri.weight} {ingItem?.unit || ri.ingredient_unit || 'kg'}
                                                                    </TableCell>
                                                                    <TableCell align="center">
                                                                        <TextField
                                                                            size="small"
                                                                            type="number"
                                                                            placeholder="0.00"
                                                                            value={editVal}
                                                                            onChange={(e) => handleWeightChange(ingId, e.target.value)}
                                                                            InputProps={{
                                                                                inputProps: { step: '0.001', min: '0.001' }
                                                                            }}
                                                                            sx={{ width: 90, '& input': { py: 0.5, textAlign: 'right', fontWeight: 700 } }}
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell align="center">
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                                                            <Button
                                                                                size="small"
                                                                                variant="contained"
                                                                                color="primary"
                                                                                disabled={editVal === ''}
                                                                                onClick={() => handleSaveWeight(ingId)}
                                                                                sx={{ minWidth: 36, p: 0.5, bgcolor: '#008751', '&:hover': { bgcolor: '#006f42' } }}
                                                                                title="Save changes"
                                                                            >
                                                                                <SaveIcon sx={{ fontSize: 16 }} />
                                                                            </Button>
                                                                            <IconButton
                                                                                size="small"
                                                                                color="error"
                                                                                onClick={() => handleDeleteRecipeIng(ingId, ri.id)}
                                                                                title="Remove Ingredient"
                                                                                sx={{ p: 0.5 }}
                                                                            >
                                                                                <DeleteIcon sx={{ fontSize: 18 }} />
                                                                            </IconButton>
                                                                        </Box>
                                                                    </TableCell>
                                                                </TableRow>
                                                            );
                                                        })
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>

                                        {/* Add New Ingredient to Recipe */}
                                        <Box sx={{ mt: 3, p: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <AddIcon sx={{ color: '#008751', fontSize: 20 }} />
                                                    + Add Ingredient to Recipe (नवीन साहित्य जोडा)
                                                </Typography>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    color="success"
                                                    onClick={() => setRawMatDialogOpen(true)}
                                                    sx={{ fontWeight: 800, fontSize: '0.75rem', textTransform: 'none', py: 0.2 }}
                                                >
                                                    + नवीन कच्चा माल तयार करा
                                                </Button>
                                            </Box>
                                            <Grid container spacing={1.5} alignItems="center">
                                                <Grid item xs={12} sm={6}>
                                                    <FormControl fullWidth size="small">
                                                        <InputLabel>Choose Raw Material (कच्चा माल)</InputLabel>
                                                        <Select
                                                            value={newIngId}
                                                            label="Choose Raw Material (कच्चा माल)"
                                                            onChange={(e) => setNewIngId(e.target.value)}
                                                        >
                                                            {(inventory || []).filter(ing => {
                                                                const dishBranch = selectedDish?.branch_id || currentUser?.branch_id || 1;
                                                                return !dishBranch || !ing.branch_id || String(ing.branch_id) === String(dishBranch);
                                                            }).map(ing => (
                                                                <MenuItem key={ing.id} value={ing.id}>
                                                                    {getDisplayName(ing)} ({ing.unit})
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                <Grid item xs={12} sm={3}>
                                                    {(() => {
                                                        const curUnit = (inventory || []).find(i => parseInt(i.id) === parseInt(newIngId))?.unit || 'नग';
                                                        const isIntegerUnit = ['नग', 'pcs', 'वाटी', 'प्लेट', 'piece', 'bowl', 'plate'].includes(curUnit.toLowerCase());
                                                        return (
                                                            <TextField
                                                                fullWidth
                                                                size="small"
                                                                type="number"
                                                                label={`प्रमाण (${curUnit})`}
                                                                placeholder={isIntegerUnit ? "उदा. 3" : "उदा. 0.150"}
                                                                value={newIngWeight}
                                                                onChange={(e) => setNewIngWeight(e.target.value)}
                                                                InputProps={{
                                                                    inputProps: { step: isIntegerUnit ? '1' : '0.01', min: '0.01' }
                                                                }}
                                                            />
                                                        );
                                                    })()}
                                                </Grid>
                                                <Grid item xs={12} sm={3}>
                                                    <Button
                                                        fullWidth
                                                        variant="contained"
                                                        onClick={handleAddIngredientToRecipe}
                                                        disabled={!newIngId || !newIngWeight}
                                                        sx={{
                                                            bgcolor: '#008751',
                                                            fontWeight: 800,
                                                            textTransform: 'none',
                                                            py: 0.8,
                                                            '&:hover': { bgcolor: '#006f42' }
                                                        }}
                                                    >
                                                        + Add to Dish
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </Box>

                                        {/* Dialog to create new Raw Material */}
                                        <Dialog open={rawMatDialogOpen} onClose={() => setRawMatDialogOpen(false)} maxWidth="xs" fullWidth>
                                            <DialogTitle sx={{ fontWeight: 800, color: '#111827', pb: 1 }}>
                                                🥬 नवीन कच्चा माल तयार करा
                                            </DialogTitle>
                                            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
                                                <Typography variant="body2" sx={{ color: '#64748b' }}>
                                                    येथे तुम्ही हॉटेलमध्ये वापरला जाणारा कच्चा माल तयार करू शकता (उदा. चपाती, चिकन वाटी, अंडी, भात, पनीर).
                                                </Typography>
                                                <TextField
                                                    autoFocus
                                                    label="कच्च्या मालाचे नाव (Item Name)"
                                                    placeholder="उदा. चपाती किंवा चिकन वाटी"
                                                    value={rawMatName}
                                                    onChange={(e) => setRawMatName(e.target.value)}
                                                    fullWidth
                                                    size="small"
                                                />
                                                <FormControl fullWidth size="small">
                                                    <InputLabel>मोजणीचे युनिट (Unit)</InputLabel>
                                                    <Select
                                                        value={rawMatUnit}
                                                        label="मोजणीचे युनिट (Unit)"
                                                        onChange={(e) => setRawMatUnit(e.target.value)}
                                                    >
                                                        <MenuItem value="नग">नग (Pcs / Number)</MenuItem>
                                                        <MenuItem value="वाटी">वाटी / पोर्शन (Bowl / Portion)</MenuItem>
                                                        <MenuItem value="प्लेट">प्लेट (Plate)</MenuItem>
                                                        <MenuItem value="kg">किलो (kg)</MenuItem>
                                                        <MenuItem value="ग्रॅम">ग्रॅम (Grams)</MenuItem>
                                                        <MenuItem value="लिटर">लिटर (Litre)</MenuItem>
                                                    </Select>
                                                </FormControl>
                                                <TextField
                                                    label="सुरुवातीचा साठा (Initial / Opening Stock)"
                                                    type="number"
                                                    value={rawMatQty}
                                                    onChange={(e) => setRawMatQty(e.target.value)}
                                                    fullWidth
                                                    size="small"
                                                    helperText="सकाळी किचनमध्ये किंवा काउंटरवर किती माल उपलब्ध आहे"
                                                />
                                                <TextField
                                                    label="कमी साठा सूचना (Low Stock Alert Threshold)"
                                                    type="number"
                                                    value={rawMatThreshold}
                                                    onChange={(e) => setRawMatThreshold(e.target.value)}
                                                    fullWidth
                                                    size="small"
                                                />
                                            </DialogContent>
                                            <DialogActions sx={{ p: 2, pt: 0 }}>
                                                <Button onClick={() => setRawMatDialogOpen(false)} sx={{ fontWeight: 700, color: '#64748b' }}>
                                                    रद्द करा
                                                </Button>
                                                <Button 
                                                    variant="contained" 
                                                    onClick={handleCreateRawMaterial} 
                                                    sx={{ bgcolor: '#008751', fontWeight: 800, '&:hover': { bgcolor: '#006f42' } }}
                                                >
                                                    सेव्ह करा
                                                </Button>
                                            </DialogActions>
                                        </Dialog>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}


            {activeTab === 'preset' && (
                <Card sx={{ borderRadius: 3, border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', maxWidth: 650 }}>
                    <CardContent sx={{ p: 4 }}>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#111827', mb: 2 }}>
                            Store Layout & Menu Preset Templates
                        </Typography>

                        <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
                            <Typography sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <WarningIcon /> Danger Zone
                            </Typography>
                            Applying a preset template will completely overwrite and replace all categories, menu items, recipes, tables, and raw material inventory settings in this active store. All active/pending orders will be deleted.
                        </Alert>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Choose Template Preset</InputLabel>
                                <Select
                                    value={selectedPreset}
                                    onChange={(e) => setSelectedPreset(e.target.value)}
                                    label="Choose Template Preset"
                                >
                                    <MenuItem value="multi-cuisine">Multi-Cuisine Template (Chinese, Tandoor, Indian, Beverages)</MenuItem>
                                    <MenuItem value="thali-special">Thali Special Template (Maharashtrian Special Thalis, Sweets)</MenuItem>
                                    <MenuItem value="blank">Blank Template (Start Fresh with no items/categories)</MenuItem>
                                </Select>
                            </FormControl>

                            <Button
                                variant="contained"
                                color="error"
                                onClick={handleApplyPresetTemplate}
                                disabled={presetLoading}
                                sx={{ fontWeight: 800, height: 44, bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' } }}
                            >
                                {presetLoading ? 'Resetting Store Database...' : 'Apply & Reset Menu Database'}
                            </Button>
                        </Box>
                    </CardContent>
                </Card>
            )}

            {/* Category Management Dialog */}
            <Dialog
                open={categoryDialogOpen}
                onClose={() => setCategoryDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ fontWeight: 800, borderBottom: '1px solid #e5e7eb', pb: 2 }}>
                    Manage Menu Categories (कॅटेगरी व्यवस्थापन)
                </DialogTitle>
                <DialogContent sx={{ pt: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* Add Category Form */}
                    <Box component="form" onSubmit={handleCreateCategory} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end', width: '100%', flexWrap: 'wrap' }}>
                        <TextField
                            size="small"
                            label="Category Name (English)"
                            required
                            value={newCategoryName}
                            onChange={(e) => setNewCategoryName(e.target.value)}
                            inputProps={{ autoComplete: 'off', autoCorrect: 'off', spellCheck: 'false', style: { touchAction: 'manipulation' } }}
                            sx={{ flexGrow: 1 }}
                        />
                        <TextField
                            size="small"
                            label="Category Name (Marathi/Hindi)"
                            value={newCategoryNameMr}
                            onChange={(e) => setNewCategoryNameMr(e.target.value)}
                            inputProps={{ autoComplete: 'off', autoCorrect: 'off', spellCheck: 'false', style: { touchAction: 'manipulation' } }}
                            sx={{ flexGrow: 1 }}
                        />
                        <TextField
                            size="small"
                            type="number"
                            label="Display Order (क्रम)"
                            value={newCategoryDisplayOrder}
                            onChange={(e) => setNewCategoryDisplayOrder(e.target.value)}
                            inputProps={{ autoComplete: 'off', autoCorrect: 'off', style: { touchAction: 'manipulation' } }}
                            sx={{ width: 110 }}
                        />
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={categorySaveLoading}
                            sx={{ bgcolor: '#008751', '&:hover': { bgcolor: '#006f42' }, fontWeight: 800, height: 40, px: 3, borderRadius: 2 }}
                        >
                            Add
                        </Button>
                    </Box>

                    <Divider />

                    {/* Categories List */}
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#374151', mb: -1 }}>
                        Existing Categories ({activeBranchCategories.length})
                    </Typography>

                    {activeBranchCategories.length === 0 ? (
                        <Alert severity="info" sx={{ borderRadius: 2 }}>
                            No categories created yet. Please add a category above to get started.
                        </Alert>
                    ) : (
                        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e5e7eb', borderRadius: 2, maxHeight: 300, overflowY: 'auto' }}>
                            <Table size="small" stickyHeader>
                                <TableHead sx={{ bgcolor: '#f9fafb' }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 800 }}>Order</TableCell>
                                        <TableCell sx={{ fontWeight: 800 }}>Name (English)</TableCell>
                                        <TableCell sx={{ fontWeight: 800 }}>Name (Marathi/Hindi)</TableCell>
                                        <TableCell sx={{ fontWeight: 800 }} align="center">Action</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(() => {
                                        const sortedCats = [...activeBranchCategories].sort((a, b) => (parseInt(a.display_order) || 0) - (parseInt(b.display_order) || 0));
                                        return sortedCats.map((cat) => {
                                            const isEditing = editingCategory && editingCategory.id === cat.id;
                                            return (
                                                <TableRow key={cat.id}>
                                                    <TableCell sx={{ fontWeight: 800, width: 80 }}>
                                                        {isEditing ? (
                                                            <TextField
                                                                size="small"
                                                                type="number"
                                                                value={editCategoryDisplayOrder}
                                                                onChange={(e) => setEditCategoryDisplayOrder(e.target.value)}
                                                                sx={{ width: 60, '& input': { py: 0.5, fontSize: '0.85rem' } }}
                                                            />
                                                        ) : (
                                                            <Chip label={cat.display_order || 0} size="small" sx={{ fontWeight: 800, height: 20 }} />
                                                        )}
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 700 }}>
                                                        {isEditing ? (
                                                            <TextField
                                                                size="small"
                                                                value={editCategoryName}
                                                                onChange={(e) => setEditCategoryName(e.target.value)}
                                                                fullWidth
                                                                sx={{ '& input': { py: 0.5, fontSize: '0.9rem' } }}
                                                            />
                                                        ) : (
                                                            cat.name
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {isEditing ? (
                                                            <TextField
                                                                size="small"
                                                                value={editCategoryNameMr}
                                                                onChange={(e) => setEditCategoryNameMr(e.target.value)}
                                                                fullWidth
                                                                sx={{ '& input': { py: 0.5, fontSize: '0.9rem' } }}
                                                            />
                                                        ) : (
                                                            cat.name_mr || '-'
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        {isEditing ? (
                                                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                                                <IconButton
                                                                    color="success"
                                                                    onClick={handleUpdateCategory}
                                                                    size="small"
                                                                >
                                                                    <CheckIcon fontSize="small" />
                                                                </IconButton>
                                                                <IconButton
                                                                    color="error"
                                                                    onClick={() => setEditingCategory(null)}
                                                                    size="small"
                                                                >
                                                                    <CloseIcon fontSize="small" />
                                                                </IconButton>
                                                            </Box>
                                                        ) : (
                                                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                                                <IconButton
                                                                    color="primary"
                                                                    onClick={() => {
                                                                        setEditingCategory(cat);
                                                                        setEditCategoryName(cat.name);
                                                                        setEditCategoryNameMr(cat.name_mr || '');
                                                                        setEditCategoryDisplayOrder(cat.display_order || 0);
                                                                    }}
                                                                    size="small"
                                                                >
                                                                    <EditIcon fontSize="small" />
                                                                </IconButton>
                                                                <IconButton
                                                                    color="error"
                                                                    onClick={() => handleDeleteCategory(cat.id)}
                                                                    size="small"
                                                                >
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            </Box>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        });
                                    })()}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2.5, borderTop: '1px solid #e5e7eb' }}>
                    <Button
                        onClick={() => setCategoryDialogOpen(false)}
                        sx={{ color: '#475569', fontWeight: 800 }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dish Add/Edit Form Dialog */}
            <Dialog
                open={dishFormOpen}
                onClose={() => setDishFormOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <form onSubmit={handleDishFormSubmit}>
                    <DialogTitle sx={{ fontWeight: 800, borderBottom: '1px solid #e5e7eb', pb: 2 }}>
                        {editingDish ? 'Edit Dish Details' : 'Add New Menu Item'}
                    </DialogTitle>
                    <DialogContent sx={{ pt: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        {/* Row 1: Name English */}
                        <Box sx={{ width: '100%' }}>
                            <TextField
                                fullWidth
                                label="Dish Name (English)"
                                required
                                value={dishForm.name}
                                onChange={(e) => setDishForm(prev => ({ ...prev, name: e.target.value }))}
                            />
                        </Box>

                        {/* Row 2: Marathi & Hindi */}
                        <Box sx={{ display: 'flex', gap: 2.5, width: '100%' }}>
                            <TextField
                                fullWidth
                                label="Dish Name (Marathi)"
                                value={dishForm.name_mr}
                                onChange={(e) => setDishForm(prev => ({ ...prev, name_mr: e.target.value }))}
                            />
                            <TextField
                                fullWidth
                                label="Dish Name (Hindi)"
                                value={dishForm.name_hi}
                                onChange={(e) => setDishForm(prev => ({ ...prev, name_hi: e.target.value }))}
                            />
                        </Box>

                        {/* Row 3: Category, Price & Display Order */}
                        <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                            <FormControl fullWidth required>
                                <InputLabel id="dish-category-label">Category</InputLabel>
                                <Select
                                    labelId="dish-category-label"
                                    value={dishForm.category_id !== undefined && dishForm.category_id !== null ? String(dishForm.category_id) : ''}
                                    label="Category"
                                    onChange={(e) => handleDishCategoryChange(e.target.value)}
                                >
                                    {(activeBranchCategories && Array.isArray(activeBranchCategories) ? activeBranchCategories : []).map(cat => (
                                        <MenuItem key={cat.id} value={String(cat.id)}>
                                            {cat.name} {cat.name_mr ? `(${cat.name_mr})` : ''}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <TextField
                                fullWidth
                                label="Price (₹)"
                                required
                                type="number"
                                inputProps={{ min: 0, step: 0.01 }}
                                value={dishForm.price}
                                onChange={(e) => setDishForm(prev => ({ ...prev, price: e.target.value }))}
                            />
                            <TextField
                                fullWidth
                                label="Display Order (क्रम)"
                                type="number"
                                value={dishForm.display_order || 0}
                                onChange={(e) => setDishForm(prev => ({ ...prev, display_order: parseInt(e.target.value) || 0 }))}
                            />
                        </Box>

                        {/* Row 4: Image URL with live preview */}
                        <Box sx={{ width: '100%' }}>
                            <TextField
                                fullWidth
                                label="Image URL / Relative Path"
                                placeholder="https://example.com/image.jpg किंवा images/veg_biryani.jpg paste करा"
                                value={dishForm.image_url}
                                onChange={(e) => setDishForm(prev => ({ ...prev, image_url: e.target.value }))}
                                InputProps={{
                                    endAdornment: dishForm.image_url ? (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => setDishForm(prev => ({ ...prev, image_url: '' }))}>
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : null
                                }}
                            />
                            {dishForm.image_url && (
                                <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Box
                                        component="img"
                                        src={dishForm.image_url}
                                        alt="Preview"
                                        onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                                        sx={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 2, border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
                                    />
                                    <Box sx={{ display: 'none', width: 80, height: 80, borderRadius: 2, border: '1px dashed #e5e7eb', alignItems: 'center', justifyContent: 'center', bgcolor: '#f9fafb' }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', fontSize: '0.7rem' }}>Image<br/>not found</Typography>
                                    </Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                                        ✅ Preview — URL valid असेल तर image दिसेल
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        {/* Row 5: Switch */}
                        <Box sx={{ width: '100%' }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={dishForm.is_available}
                                        onChange={(e) => setDishForm(prev => ({ ...prev, is_available: e.target.checked }))}
                                        color="primary"
                                    />
                                }
                                label="Available for Ordering"
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: 2.5, borderTop: '1px solid #e5e7eb' }}>
                        <Button
                            onClick={() => setDishFormOpen(false)}
                            sx={{ color: '#475569', fontWeight: 700 }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            sx={{ bgcolor: '#008751', '&:hover': { bgcolor: '#006f42' }, fontWeight: 800 }}
                        >
                            Save
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* CSV Import Validation & Preview Dialog */}
            <Dialog
                open={csvPreviewOpen}
                onClose={() => setCsvPreviewOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ fontWeight: 800, borderBottom: '1px solid #e5e7eb', pb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FileUploadIcon sx={{ color: '#008751' }} /> CSV Validation & Import Preview (अपलोड पूर्व-तपासणी)
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <Alert severity="info" sx={{ mb: 2, borderRadius: 2, fontWeight: 600 }}>
                        एकूण **{csvParsedItems.length}** पदार्थ सापडले. खालील बदलावांची तपासणी करा आणि **"Confirm & Import"** बटण दाबा.
                    </Alert>

                    <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e5e7eb', borderRadius: 2, maxHeight: 350 }}>
                        <Table size="small" stickyHeader>
                            <TableHead sx={{ bgcolor: '#f9fafb' }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 800 }}>Action Status</TableCell>
                                    <TableCell sx={{ fontWeight: 800 }}>Category Name</TableCell>
                                    <TableCell sx={{ fontWeight: 800 }}>English Name</TableCell>
                                    <TableCell sx={{ fontWeight: 800 }}>Marathi Name</TableCell>
                                    <TableCell sx={{ fontWeight: 800 }} align="right">Price (₹)</TableCell>
                                    <TableCell sx={{ fontWeight: 800 }} align="center">Veg/Non-Veg</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {csvParsedItems.map((item, idx) => {
                                    const isUpdate = item.id && menuItems.some(m => m.id == item.id);
                                    return (
                                        <TableRow key={idx}>
                                            <TableCell>
                                                <Chip
                                                    label={isUpdate ? `Update (ID: ${item.id})` : 'New Dish (ॲड)'}
                                                    size="small"
                                                    color={isUpdate ? 'primary' : 'success'}
                                                    sx={{ fontWeight: 800, height: 20 }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>{item.category_name}</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>{item.name}</TableCell>
                                            <TableCell>{item.name_mr}</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 800, color: '#008751' }}>
                                                ₹{item.price.toFixed(2)}
                                            </TableCell>
                                            <TableCell align="center">
                                                {item.veg ? '🟢 Veg' : '🔴 Non-Veg'}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions sx={{ p: 2.5, borderTop: '1px solid #e5e7eb' }}>
                    <Button
                        onClick={() => setCsvPreviewOpen(false)}
                        sx={{ color: '#475569', fontWeight: 800 }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disabled={csvImportLoading}
                        onClick={handleConfirmCsvImport}
                        sx={{ bgcolor: '#008751', '&:hover': { bgcolor: '#006f42' }, fontWeight: 800, px: 3, borderRadius: 2 }}
                    >
                        {csvImportLoading ? 'Importing & Syncing DB...' : 'Confirm & Import to Database'}
                    </Button>
                </DialogActions>
            </Dialog>

            {activeTab === 'tables' && (
                <TableSectionManager />
            )}
        </Box>
    );
}
